import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  Group,
  Loader,
  NumberInput,
  Pagination,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip as MantineTooltip,
  Modal,
  MultiSelect,
  SegmentedControl,
  Textarea,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  ChevronDown,
  CircleHelp,
  ChartLine as ChartLineIcon,
  Download,
  Filter,
  Clock3,
  List as ListIcon,
  Rows3,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import { spectralAnalysisStore } from '../../features/research/store/spectralAnalysisStore';
import { buildSpectralAnalysisSnapshot } from '../../features/research/services/analysisBuilder';
import {
  forwardBackwardLowPass,
  runSpectralAnalysis,
  zScoreNormalize,
} from '../../features/research/utils/spectralAnalysis';
import { type EffectiveSessionDataSource } from '../../features/research/services/resolveEffectiveSessionValidations';
import {
  useEffectiveSessionHydration,
  type SessionHydrationPatch,
} from '../../features/research/hooks/useEffectiveSessionHydration';
import { useTabAwareMemo } from '../../features/research/hooks/useTabAwareMemo';
import { computeOverviewEventMetrics } from '../../features/research/utils/computeOverviewEventMetrics';
import { SpectralOverviewTab } from '../../features/research/components/spectral/SpectralOverviewTab';
import type {
  SpectralAnalysisSnapshot,
  FeatureKey,
  ValidatedSessionAnalysis,
  ExcludedSessionReason,
} from '../../features/research/types';
import type { HeartRateEvent } from '@model/DeviceConfiguration';
import { getConfiguration } from '../../services/configurationManager';
import {
  analyzeSessionHrData,
  getCachedSessionHrDataAnalysis,
  invalidateSessionHrDataAnalysisCache,
  isOpenAiRepairConfigured,
  repairSessionHrDataWithAi,
  type SessionHrDataAnalysisResult,
} from '../../features/research/ai-repair';
import {
  buildTrendSegmentValueMap,
  getSessionTrackOriginTimestamp,
  eventToRelativeSecond,
  type TrendSegmentKey,
} from '../../utils/hrDataRepair';
import {
  formatFromSecondToMinutes,
  formatTimestampToDateTimeWithMs,
  formatTimestampToTimeWithMs,
} from '../../utils/formatDuration';
import { useAuthUser } from '../../store/useAuthStore';
import { useResearchConfigurationsLiteQuery } from '../../hooks/queries/useResearchConfigurationsLiteQuery';

const formatDate = (value?: string | Date | null) =>
  value ? new Date(value).toLocaleString('it-IT') : 'n/a';

const hasBaselineValue = (baseline: unknown) =>
  (typeof baseline === 'number' && Number.isFinite(baseline) && baseline > 0) ||
  (typeof baseline === 'string' &&
    baseline.trim().length > 0 &&
    baseline.trim() !== '0');

const formatRepairMetric = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '-';

const formatRepairPercent = (value: number) => `${value.toFixed(1)}%`;

const formatStatValue = (value: number | null, suffix = '') =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value.toFixed(2)}${suffix}`
    : 'n/a';

const meanOf = (values: number[]) =>
  values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;

const sampleStdOf = (values: number[], mean: number | null) => {
  if (values.length < 2 || mean == null) return null;

  return Math.sqrt(
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
      (values.length - 1)
  );
};

const formatSessionEventsDuration = (events?: HeartRateEvent[]) => {
  const timestamps = (events ?? [])
    .map(event => event.timestamp)
    .filter(
      (timestamp): timestamp is number =>
        typeof timestamp === 'number' && Number.isFinite(timestamp)
    );

  if (timestamps.length < 2) return 'n/a';

  const durationSeconds = Math.round(
    (Math.max(...timestamps) - Math.min(...timestamps)) / 1000
  );

  return formatFromSecondToMinutes(Math.max(0, durationSeconds));
};

const formatExportDateForFilename = (value: Date) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  const hours = `${value.getHours()}`.padStart(2, '0');
  const minutes = `${value.getMinutes()}`.padStart(2, '0');
  return `${year}${month}${day}_${hours}${minutes}`;
};

const sanitizeFilenamePart = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const significanceColor = (significant: boolean) =>
  significant ? 'teal' : 'gray';

const StatHelp = ({
  label,
  description,
}: {
  label: string;
  description: string;
}) => (
  <Group gap={6} wrap='nowrap'>
    <Text size='sm' fw={500}>
      {label}
    </Text>
    <MantineTooltip multiline w={280} label={description}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'help',
          color: 'var(--mantine-color-dimmed)',
        }}
      >
        <CircleHelp size={14} />
      </span>
    </MantineTooltip>
  </Group>
);

const featureKeys: FeatureKey[] = ['HR', 'HRV', 'LF', 'HF'];

type HrvQcState = {
  reviewed: boolean;
  notes: string;
};

type SegmentFeatureRow = {
  second: number;
  mean: number;
  stdUpper: number;
  stdLower: number;
  [key: string]: number | string | null;
};

type StatisticalSegmentKey = 'full' | 'pre' | 'session';

type DescriptiveStatRow = {
  feature: FeatureKey;
  sessionCount: number;
  mean: number | null;
  std: number | null;
  min: number | null;
  max: number | null;
  coefficientOfVariation: number | null;
  outlierSessions: Array<{
    sessionId: number;
    patientId: string;
    value: number;
    distance: number;
  }>;
};

type SessionFeatureMean = {
  sessionId: number;
  patientId: string;
  value: number;
};

const usesHrvPreprocessing = (
  summary: SpectralAnalysisSnapshot['includedSessions'][number]['spectral']['preprocessingSummary']
) => summary.preprocessingVersion === 2;

const SPECTRA_SECTION_TITLE = 'PSD tachogramma NN (pre/post VR)';

const SPECTRA_SECTION_CAPTION =
  'Welch sulla serie detrendata e ricampionata a 4 Hz (HRV preprocessing, tachogramma NN da HR). Il rapporto LF/HF in tabella sessioni deriva dall’integrazione nelle bande su questa PSD, non dai canali lf/hf del Polar. Vedi guida utente § 11.6.';

const maxCorrectedBeatPercent = (
  summary: SpectralAnalysisSnapshot['includedSessions'][number]['spectral']['preprocessingSummary']
) =>
  Math.max(
    summary.correctedBeatPercentBaseline,
    summary.correctedBeatPercentExposure
  );

const trendFeatureLabels: Record<FeatureKey, string> = {
  HR: 'HR',
  HRV: 'HRV',
  LF: 'LF',
  HF: 'HF',
};

const statisticalSegmentLabels: Record<StatisticalSegmentKey, string> = {
  full: 'Tutto il segnale',
  pre: 'Pre-VR (Baseline)',
  session: 'Sessione',
};

const TREND_SECTION_HELP_TEXT =
  'Questa vista mostra l’andamento temporale dei segnali su dati effettivi ripuliti. La linea principale rappresenta la media del gruppo; la banda attorno alla linea mostra quanto i soggetti sono variabili tra loro. Se attivi la sessione selezionata, puoi confrontare il singolo soggetto con la media del gruppo.';

const getTrendFeatureInterpretation = (
  feature: FeatureKey,
  normalized: boolean,
  lowPassFiltered: boolean
) => {
  if (normalized) {
    return {
      short: `${trendFeatureLabels[feature]} è mostrato in forma normalizzata per rendere più confrontabile l’andamento tra soggetti.`,
      detail: lowPassFiltered
        ? 'In questa modalità conta soprattutto la forma del cambiamento nel tempo. Il filtro passabasso smussa le oscillazioni più rumorose, quindi i valori non vanno letti come misura assoluta ma come scostamento relativo più leggibile.'
        : 'In questa modalità conta soprattutto la forma del cambiamento nel tempo. I valori non vanno letti come misura assoluta, ma come scostamento relativo rispetto al profilo della singola sessione.',
    };
  }

  if (feature === 'HR') {
    return {
      short:
        'HR è mostrato in valori reali, quindi il livello assoluto è facilmente interpretabile.',
      detail: lowPassFiltered
        ? 'Esempio: 62 bpm, 78 bpm, 90 bpm. In più, con il filtro passabasso attivo, la curva viene smussata per ridurre il rumore visivo.'
        : 'Esempio: 62 bpm, 78 bpm, 90 bpm. Per HR il valore assoluto è già utile da leggere anche senza normalizzazione.',
    };
  }

  return {
    short: `${trendFeatureLabels[feature]} è mostrato in valori reali, ma può avere scale molto diverse tra soggetti.`,
    detail: lowPassFiltered
      ? `Per ${trendFeatureLabels[feature]} è spesso più utile osservare la forma del cambiamento nel tempo che confrontare solo i numeri assoluti tra persone diverse. Il filtro passabasso aiuta a leggere meglio il profilo medio del gruppo.`
      : `Per ${trendFeatureLabels[feature]} è spesso più utile osservare la forma del cambiamento nel tempo che confrontare solo i numeri assoluti tra persone diverse.`,
  };
};

const TREND_SIGNAL_TOOLTIP =
  'Scegli la feature da osservare. I toggle accanto ti permettono di attivare normalizzazione e filtro passabasso sulla vista corrente del grafico.';

const TREND_NORMALIZATION_TOOLTIP =
  'Normalizzazione z-score: per ogni sessione si sottrae la media della traccia e si divide per la sua deviazione standard.';

const TREND_LOW_PASS_TOOLTIP =
  'Filtro passabasso forward-backward: smussa il segnale riducendo le oscillazioni più rapide e rumorose senza introdurre sfasamento visibile.';

const TREND_NORMALIZATION_DISCLAIMER =
  'Regola di normalizzazione applicata: per ogni sessione si calcola uno z-score sulla traccia mostrata, sottraendo la media della sessione e dividendo per la sua deviazione standard. In questa vista conta quindi la forma relativa del cambiamento, non il valore assoluto originale.';

const TREND_LOW_PASS_DISCLAIMER =
  'Filtro passabasso applicato: la curva viene smussata con lo stesso low-pass già usato nella pipeline analitica, per ridurre il rumore visivo e rendere più leggibile l’andamento medio del segnale.';

const TREND_EXPORT_DISCLAIMER =
  'L’export CSV replica esattamente la vista corrente del grafico: stessa feature, stessa eventuale normalizzazione e stesso eventuale filtro passabasso.';

const TREND_VIEW_LOW_PASS_CUTOFF_HZ = 0.12;
const TREND_TIME_TICK_SECONDS = 300;
const OVERLAY_SELECT_ALL_VALUE = 'select-all';
const OVERLAY_SESSION_COLORS = [
  '#dc2626',
  '#16a34a',
  '#9333ea',
  '#ea580c',
  '#0891b2',
  '#be123c',
  '#4f46e5',
  '#65a30d',
  '#c026d3',
  '#0f766e',
  '#b45309',
  '#2563eb',
];

const getOverlaySeriesKey = (sessionId: number) => `session_${sessionId}`;

const getOverlayColor = (sessionId: number) =>
  OVERLAY_SESSION_COLORS[Math.abs(sessionId) % OVERLAY_SESSION_COLORS.length];

const buildTrendTimeTicks = (
  rows: Array<{ second?: number | string | null }>
) => {
  if (!rows.length) return [];

  const seconds = rows.map(row => Number(row.second)).filter(Number.isFinite);
  if (!seconds.length) return [];

  const minSecond = Math.min(...seconds);
  const maxSecond = Math.max(...seconds);
  const firstTick =
    Math.ceil(minSecond / TREND_TIME_TICK_SECONDS) * TREND_TIME_TICK_SECONDS;
  const ticks: number[] = [];

  for (
    let second = firstTick;
    second <= maxSecond;
    second += TREND_TIME_TICK_SECONDS
  ) {
    ticks.push(second);
  }

  return ticks;
};

const CNR_CALABRIA_RESEARCH_EMAIL = 'cnr-calabria@become-hub.com';

type RecalculatedSessionState =
  | {
      source: 'repaired' | 'effective';
      validation: ValidatedSessionAnalysis;
      excluded?: undefined;
    }
  | {
      source: 'repaired' | 'effective';
      validation?: undefined;
      excluded: ExcludedSessionReason;
    };

type SessionAnalysisState =
  | { status: 'idle' }
  | {
      status: 'clean';
      summary?: string;
      findings?: string[];
    }
  | {
      status: 'needs_repair';
      summary?: string;
      findings?: string[];
      duplicateCount: number;
      gapCount: number;
    }
  | { status: 'failed'; message: string };

const getAnalysisServiceErrorMessage = (error: Error): string => {
  const message = error.message;
  if (
    message.includes('API key is not configured') ||
    message.includes('invalid_api_key') ||
    message.includes('Incorrect API key') ||
    message.includes('401') ||
    message.includes('OpenAI') ||
    message.includes('API error')
  ) {
    return 'Servizio esterno non disponibile. Riavvia il dev server dopo aver aggiornato la configurazione.';
  }
  return message;
};

const mapAnalysisResultToState = (
  result: SessionHrDataAnalysisResult
): Exclude<SessionAnalysisState, { status: 'idle' }> => {
  if (result.status === 'failed') {
    return {
      status: 'failed',
      message: result.message ?? 'Analisi non riuscita.',
    };
  }

  if (result.status === 'clean') {
    return {
      status: 'clean',
      summary: result.summary,
      findings: result.findings,
    };
  }

  return {
    status: 'needs_repair',
    summary: result.summary,
    findings: result.findings,
    duplicateCount: result.duplicateCount ?? 0,
    gapCount: result.gapCount ?? 0,
  };
};

const buildEventIndicatorFromCounts = (
  duplicateCount: number,
  gapCount: number
) => {
  const hasGap = gapCount > 0;
  const hasDuplicate = duplicateCount > 0;

  if (hasGap && hasDuplicate) {
    return { color: 'orange', label: 'gap + duplicati' };
  }
  if (hasGap) {
    return { color: 'yellow', label: 'gap' };
  }
  if (hasDuplicate) {
    return { color: 'blue', label: 'duplicati' };
  }
  return { color: 'teal', label: 'nessun problema' };
};

const getSessionEventBadge = ({
  analysis,
  isRepaired,
  isExcluded,
  isLoading,
}: {
  analysis?: SessionAnalysisState;
  isRepaired: boolean;
  isExcluded: boolean;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return {
      color: 'gray',
      label: 'Caricamento…',
      variant: 'light' as const,
    };
  }
  if (isExcluded) {
    return { color: 'gray', label: 'ESCLUSA', variant: 'outline' as const };
  }
  if (isRepaired) {
    return { color: 'teal', label: 'riparata', variant: 'light' as const };
  }
  if (analysis?.status === 'clean') {
    return {
      color: 'teal',
      label: 'Record perfetto',
      variant: 'light' as const,
    };
  }
  if (analysis?.status === 'needs_repair') {
    return {
      ...buildEventIndicatorFromCounts(
        analysis.duplicateCount,
        analysis.gapCount
      ),
      variant: 'outline' as const,
    };
  }
  if (analysis?.status === 'failed') {
    return {
      color: 'red',
      label: 'Analisi fallita',
      variant: 'light' as const,
    };
  }
  return {
    color: 'gray',
    label: 'Non analizzata',
    variant: 'light' as const,
  };
};

const formatTrendTooltipLabel = (label: string | number) => {
  const second = Number(label);

  if (Number.isFinite(second)) {
    const minutes = Math.floor(second / 60);
    const seconds = second % 60;
    return `Timestamp ${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  return 'Timestamp non disponibile';
};

const formatTrendTooltipMetric = (value: number | string) => {
  const numericValue =
    typeof value === 'number' ? value : Number.parseFloat(value);

  if (!Number.isFinite(numericValue)) {
    return value;
  }

  return new Intl.NumberFormat('it-IT', {
    maximumSignificantDigits: 2,
  }).format(numericValue);
};

const normalizeTrendSeries = (values: Array<number | undefined>) => {
  const finiteValues = values.filter(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value)
  );

  if (finiteValues.length === 0) {
    return values.map(() => undefined);
  }

  const mean =
    finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length;
  const variance =
    finiteValues.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
    Math.max(finiteValues.length - 1, 1);
  const std = Math.sqrt(Math.max(variance, 0));

  if (std === 0) {
    return values.map(value => (typeof value === 'number' ? 0 : undefined));
  }

  return values.map(value =>
    typeof value === 'number' && Number.isFinite(value)
      ? (value - mean) / std
      : undefined
  );
};

const normalizeTrendMap = (valueMap: Map<number, number>) => {
  if (valueMap.size === 0) return valueMap;

  const orderedEntries = [...valueMap.entries()].sort((a, b) => a[0] - b[0]);
  const normalizedValues = normalizeTrendSeries(
    orderedEntries.map(([, value]) => value)
  );

  return new Map(
    orderedEntries.map(([second], index) => [
      second,
      normalizedValues[index] ?? 0,
    ])
  );
};

const lowPassTrendSeries = (values: number[]) => {
  if (values.length < 3) return values;
  return forwardBackwardLowPass(values, TREND_VIEW_LOW_PASS_CUTOFF_HZ, 1);
};

const lowPassTrendMap = (valueMap: Map<number, number>) => {
  if (valueMap.size < 3) return valueMap;

  const orderedEntries = [...valueMap.entries()].sort((a, b) => a[0] - b[0]);
  const filteredValues = lowPassTrendSeries(
    orderedEntries.map(([, value]) => value)
  );

  return new Map(
    orderedEntries.map(([second], index) => [
      second,
      filteredValues[index] ?? 0,
    ])
  );
};

const applyTrendProcessingToSeries = (
  values: Array<number | undefined>,
  normalized: boolean,
  lowPassFiltered: boolean
) => {
  let nextValues = values.map(value =>
    typeof value === 'number' && Number.isFinite(value) ? value : 0
  );

  if (normalized) {
    nextValues = zScoreNormalize(nextValues);
  }

  if (lowPassFiltered) {
    nextValues = lowPassTrendSeries(nextValues);
  }

  return nextValues;
};

const applyTrendProcessingToMap = (
  valueMap: Map<number, number>,
  normalized: boolean,
  lowPassFiltered: boolean
) => {
  let nextMap = valueMap;

  if (normalized) {
    nextMap = normalizeTrendMap(nextMap);
  }

  if (lowPassFiltered) {
    nextMap = lowPassTrendMap(nextMap);
  }

  return nextMap;
};

export function SpectralNormalizationDetailPage() {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const authUser = useAuthUser();
  const [exportOpened, { open: openExportModal, close: closeExportModal }] =
    useDisclosure(false);
  const [
    editSessionsOpened,
    { open: openEditSessionsModal, close: closeEditSessionsModal },
  ] = useDisclosure(false);
  const [
    trendHelpOpened,
    { open: openTrendHelpModal, close: closeTrendHelpModal },
  ] = useDisclosure(false);
  const [snapshot, setSnapshot] = useState<SpectralAnalysisSnapshot | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const handleTabChange = useCallback((value: string | null) => {
    if (value) setActiveTab(value);
  }, []);

  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [selectedOverlaySessionIds, setSelectedOverlaySessionIds] = useState<
    string[]
  >([]);
  const [selectedTrendFeature, setSelectedTrendFeature] =
    useState<FeatureKey>('HR');
  const [trendNormalizationEnabled, setTrendNormalizationEnabled] =
    useState(false);
  const [trendLowPassEnabled, setTrendLowPassEnabled] = useState(false);
  const [trendSegmentTab, setTrendSegmentTab] = useState<
    'full' | 'pre' | 'post'
  >('full');
  const [statisticalSegmentTab, setStatisticalSegmentTab] =
    useState<StatisticalSegmentKey>('full');
  const [segmentWindowMinutes] = useState(5);
  const [trendStart, setTrendStart] = useState<string | null>(null);
  const [trendEnd, setTrendEnd] = useState<string | null>(null);
  const [preStart, setPreStart] = useState<string | null>(null);
  const [preEnd, setPreEnd] = useState<string | null>(null);
  const [postStart, setPostStart] = useState<string | null>(null);
  const [postEnd, setPostEnd] = useState<string | null>(null);
  const [spectraStart, setSpectraStart] = useState<string | null>(null);
  const [spectraEnd, setSpectraEnd] = useState<string | null>(null);
  const [logScale, setLogScale] = useState(false);
  const [showOverlaySessions, setShowOverlaySessions] = useState(false);
  const [outlierFilter, setOutlierFilter] = useState<string | null>(null);
  const [editSelectedSessionIds, setEditSelectedSessionIds] = useState<
    number[]
  >([]);
  const [editSearchId, setEditSearchId] = useState('');
  const [editSearchPatient, setEditSearchPatient] = useState('');
  const [editSearchDate, setEditSearchDate] = useState('');
  const [editPage, setEditPage] = useState(1);
  const [isUpdatingSessions, setIsUpdatingSessions] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState<number | null>(
    null
  );
  const [isExporting, setIsExporting] = useState(false);
  const [exportSelectedSections, setExportSelectedSections] = useState<
    string[]
  >([
    'overview',
    'sessions',
    'trend-hr',
    'trend-hrv',
    'trend-lf',
    'trend-hf',
    'spectra',
  ]);
  const [repairingSessionId, setRepairingSessionId] = useState<number | null>(
    null
  );
  const [repairPreviewSessionId, setRepairPreviewSessionId] = useState<
    number | null
  >(null);
  const [repairPreviewView, setRepairPreviewView] = useState<'graph' | 'list'>(
    'list'
  );
  const [repairPreviews, setRepairPreviews] = useState<
    Record<
      number,
      {
        repairedOnly: HeartRateEvent[];
        repairedEvents: HeartRateEvent[];
        originalCount: number;
        repairedCount: number;
      }
    >
  >({});
  const [recalculatedSessions, setRecalculatedSessions] = useState<
    Record<number, RecalculatedSessionState>
  >({});
  const [sessionAnalysisById, setSessionAnalysisById] = useState<
    Record<number, SessionAnalysisState>
  >({});
  const [analyzingSessionId, setAnalyzingSessionId] = useState<number | null>(
    null
  );
  const [effectiveValidationsLoading, setEffectiveValidationsLoading] =
    useState(false);
  const [sessionDataSourceById, setSessionDataSourceById] = useState<
    Record<number, EffectiveSessionDataSource>
  >({});
  const [effectiveEventsBySessionId, setEffectiveEventsBySessionId] = useState<
    Record<number, HeartRateEvent[]>
  >({});
  const [hrvQcBySessionId, setHrvQcBySessionId] = useState<
    Record<number, HrvQcState>
  >({});
  const overviewCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const sessionsCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const trendHrCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const trendHrvCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const trendLfCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const trendHfCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const spectraCsvRef = useRef<
    CSVLink & HTMLAnchorElement & { link?: HTMLAnchorElement }
  >(null);
  const [debouncedEditSearchId] = useDebouncedValue(editSearchId, 250);
  const [debouncedEditSearchPatient] = useDebouncedValue(
    editSearchPatient,
    250
  );
  const [debouncedEditSearchDate] = useDebouncedValue(editSearchDate, 250);
  const editSessionsPageSize = 25;
  const editSessionsParams = useMemo(
    () => ({
      status: 'completed' as const,
      page: editPage,
      size: editSessionsPageSize,
      searchId: debouncedEditSearchId,
      searchPatient: debouncedEditSearchPatient,
      searchDate: debouncedEditSearchDate,
    }),
    [
      debouncedEditSearchDate,
      debouncedEditSearchId,
      debouncedEditSearchPatient,
      editPage,
    ]
  );
  const {
    data: editConfigurationsLite,
    isLoading: editSessionsLoading,
    isFetching: editSessionsFetching,
  } = useResearchConfigurationsLiteQuery(
    authUser?.id?.toString(),
    editSessionsParams
  );
  const editFilteredSessions = editConfigurationsLite?.items ?? [];

  useEffect(() => {
    if (!analysisId) return;

    spectralAnalysisStore
      .getAnalysis(analysisId)
      .then(result => {
        setSnapshot(result);
        if (result?.includedSessions[0]) {
          setSelectedSessionId(
            result.includedSessions[0].validation.sessionId.toString()
          );
        }
      })
      .catch(error => {
        notifications.show({
          color: 'red',
          title: 'Errore caricamento analisi',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile recuperare il dettaglio dell’analisi.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [analysisId]);

  const selectedSession = useMemo(
    () =>
      snapshot?.includedSessions.find(
        item => item.validation.sessionId.toString() === selectedSessionId
      ) ?? null,
    [selectedSessionId, snapshot]
  );

  const currentAnalysisSessionIds = useMemo(
    () => snapshot?.selectedSessions ?? [],
    [snapshot]
  );

  const editAddedSessionIds = useMemo(
    () =>
      editSelectedSessionIds.filter(
        sessionId => !currentAnalysisSessionIds.includes(sessionId)
      ),
    [currentAnalysisSessionIds, editSelectedSessionIds]
  );

  const editRemovedSessionIds = useMemo(
    () =>
      currentAnalysisSessionIds.filter(
        sessionId => !editSelectedSessionIds.includes(sessionId)
      ),
    [currentAnalysisSessionIds, editSelectedSessionIds]
  );

  const getEffectiveValidation = (
    item: SpectralAnalysisSnapshot['includedSessions'][number]
  ) =>
    recalculatedSessions[item.validation.sessionId]?.validation ??
    item.validation;

  const getEffectiveExcluded = (
    item: SpectralAnalysisSnapshot['includedSessions'][number]
  ) => recalculatedSessions[item.validation.sessionId]?.excluded;

  const allOverlaySessionIds = useMemo(
    () =>
      snapshot?.includedSessions.map(item =>
        item.validation.sessionId.toString()
      ) ?? [],
    [snapshot]
  );

  const overlaySessionSelectData = useMemo(
    () => [
      { value: OVERLAY_SELECT_ALL_VALUE, label: 'Seleziona tutte' },
      ...(snapshot?.includedSessions.map(item => ({
        value: item.validation.sessionId.toString(),
        label: `${item.validation.sessionId} · ${item.validation.patientId}`,
      })) ?? []),
    ],
    [snapshot]
  );

  const activeOverlaySessionIds = useMemo(() => {
    if (!showOverlaySessions) return [];
    if (selectedOverlaySessionIds.length === 0) return allOverlaySessionIds;
    return selectedOverlaySessionIds.filter(value =>
      allOverlaySessionIds.includes(value)
    );
  }, [allOverlaySessionIds, selectedOverlaySessionIds, showOverlaySessions]);

  const activeOverlaySessionNumbers = useMemo(
    () =>
      activeOverlaySessionIds
        .map(sessionId => Number(sessionId))
        .filter(Number.isFinite),
    [activeOverlaySessionIds]
  );

  const overlayMultiSelectValue =
    showOverlaySessions && selectedOverlaySessionIds.length === 0
      ? allOverlaySessionIds
      : selectedOverlaySessionIds;

  const handleOverlaySessionChange = (values: string[]) => {
    if (values.includes(OVERLAY_SELECT_ALL_VALUE)) {
      setSelectedOverlaySessionIds(allOverlaySessionIds);
      return;
    }

    setSelectedOverlaySessionIds(values);
  };

  const overlayCompactValueLabel = useMemo(() => {
    if (overlayMultiSelectValue.length <= 3) return undefined;

    const visibleLabels = overlayMultiSelectValue
      .slice(0, 2)
      .map(value => overlaySessionSelectData.find(item => item.value === value))
      .map(item => item?.label)
      .filter(Boolean);

    const hiddenCount = overlayMultiSelectValue.length - visibleLabels.length;

    return `${visibleLabels.join(', ')} (+${hiddenCount} altre)`;
  }, [overlayMultiSelectValue, overlaySessionSelectData]);

  const shouldCompactOverlayPills = overlayMultiSelectValue.length > 3;

  const overlayDataSourceBadge = useMemo(() => {
    if (!showOverlaySessions || activeOverlaySessionNumbers.length === 0) {
      return null;
    }

    const repairedCount = activeOverlaySessionNumbers.filter(
      sessionId => sessionDataSourceById[sessionId] === 'hrData_repaired'
    ).length;

    if (repairedCount === activeOverlaySessionNumbers.length) {
      return { color: 'teal', label: 'Overlay: dati riparati' };
    }

    if (repairedCount > 0) {
      return { color: 'yellow', label: 'Overlay: dati originali e riparati' };
    }

    return { color: 'gray', label: 'Overlay: dati originali' };
  }, [activeOverlaySessionNumbers, sessionDataSourceById, showOverlaySessions]);

  const overlaySessionLabelById = useMemo(
    () =>
      new Map(
        snapshot?.includedSessions.map(item => [
          item.validation.sessionId,
          `${item.validation.sessionId} · ${item.validation.patientId}`,
        ]) ?? []
      ),
    [snapshot]
  );

  const renderOverlaySessionLines = () =>
    activeOverlaySessionNumbers.map(sessionId => (
      <Line
        key={`overlay-session-${sessionId}`}
        type='monotone'
        dataKey={getOverlaySeriesKey(sessionId)}
        stroke={getOverlayColor(sessionId)}
        strokeWidth={1.4}
        strokeOpacity={0.72}
        dot={false}
        name={overlaySessionLabelById.get(sessionId) ?? `Sessione ${sessionId}`}
      />
    ));

  const isCnrCalabriaResearchUser =
    authUser?.email === CNR_CALABRIA_RESEARCH_EMAIL;
  const segmentWindowSeconds = segmentWindowMinutes * 60;
  const trendFeatureInterpretation = getTrendFeatureInterpretation(
    selectedTrendFeature,
    trendNormalizationEnabled,
    trendLowPassEnabled
  );
  const trendModeLabel = [
    trendNormalizationEnabled ? 'normalizzato' : 'reale',
    trendLowPassEnabled ? 'filtrato-passabasso' : 'non-filtrato',
  ].join('_');
  const trendViewDescription = [
    'Vista temporale del segnale selezionato su dati effettivi (riparati se disponibili, altrimenti originali).',
    trendNormalizationEnabled
      ? 'La normalizzazione è attiva: i valori non vanno letti come unità assolute, ma come scostamenti relativi rispetto al profilo della singola sessione.'
      : 'In questa vista il segnale mantiene i suoi valori reali ripuliti.',
    trendLowPassEnabled
      ? 'Il filtro passabasso è attivo: la curva è smussata per ridurre il rumore visivo.'
      : 'Il filtro passabasso è disattivo: il grafico mostra il segnale ripulito senza smoothing aggiuntivo.',
    'Non mostra la trasformata spettrale.',
  ].join(' ');
  const trendProcessingDisclaimerText = [
    trendNormalizationEnabled ? TREND_NORMALIZATION_DISCLAIMER : null,
    trendLowPassEnabled ? TREND_LOW_PASS_DISCLAIMER : null,
  ]
    .filter(Boolean)
    .join(' ');
  const exportDate = useMemo(() => new Date(), []);
  const exportDateLabel = useMemo(
    () => exportDate.toLocaleString('it-IT'),
    [exportDate]
  );
  const exportDateFilenamePart = useMemo(
    () => formatExportDateForFilename(exportDate),
    [exportDate]
  );
  const analysisFilenamePart = useMemo(
    () => sanitizeFilenamePart(snapshot?.name || 'analisi'),
    [snapshot?.name]
  );
  const buildExportFileName = (section: string) =>
    `${analysisFilenamePart}_${section}_export-${exportDateFilenamePart}.csv`;
  const buildCsvMetadataRows = (section: string) => [
    {
      Meta: 'Titolo analisi',
      Valore: snapshot?.name ?? '',
      Sezione: section,
      'Data export': exportDateLabel,
    },
    {
      Meta: 'Data export',
      Valore: exportDateLabel,
      Sezione: section,
      'Data export': exportDateLabel,
    },
    {
      Meta: '',
      Valore: '',
      Sezione: '',
      'Data export': '',
    },
  ];

  const formatSecondLabel = (second: number) => {
    const minutes = Math.floor(second / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (second % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const getFeatureEventValue = (
    event: HeartRateEvent,
    feature: FeatureKey
  ): number | undefined => {
    switch (feature) {
      case 'HR':
        return event.hr;
      case 'HRV':
        return event.hrv ?? undefined;
      case 'LF':
        return event.lf ?? undefined;
      case 'HF':
        return event.hf ?? undefined;
      default:
        return undefined;
    }
  };

  const trendChartData = useTabAwareMemo(
    activeTab,
    ['hr', 'statistics'],
    () => {
      if (!snapshot) return [];

      const validations = snapshot.includedSessions.map(item => {
        const validation = getEffectiveValidation(item);
        return {
          sessionId: validation.sessionId,
          validation,
          baselineValues: applyTrendProcessingToSeries(
            validation.series.baseline.values[selectedTrendFeature],
            trendNormalizationEnabled,
            trendLowPassEnabled
          ),
          exposureValues: applyTrendProcessingToSeries(
            validation.series.exposure.values[selectedTrendFeature],
            trendNormalizationEnabled,
            trendLowPassEnabled
          ),
        };
      });

      return Array.from({ length: 600 }, (_, second) => {
        const segment = second < 300 ? 'baseline' : 'exposure';
        const index = segment === 'baseline' ? second : second - 300;
        const values = validations
          .map(sessionValidation =>
            segment === 'baseline'
              ? sessionValidation.baselineValues[index]
              : sessionValidation.exposureValues[index]
          )
          .filter((value): value is number => typeof value === 'number');

        const mean =
          values.length > 0
            ? values.reduce((sum, value) => sum + value, 0) / values.length
            : 0;
        const std =
          values.length > 1
            ? Math.sqrt(
                values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
                  (values.length - 1)
              )
            : 0;

        const row: Record<string, number | string | null> = {
          second,
          mean,
          stdUpper: mean + std,
          stdLower: mean - std,
          segment,
        };

        activeOverlaySessionNumbers.forEach(sessionId => {
          const sourceValidation = validations.find(
            validation => validation.sessionId === sessionId
          );
          const source =
            segment === 'baseline'
              ? sourceValidation?.baselineValues
              : sourceValidation?.exposureValues;
          row[getOverlaySeriesKey(sessionId)] = source?.[index] ?? null;
        });

        return row;
      });
    },
    [
      snapshot,
      selectedTrendFeature,
      trendNormalizationEnabled,
      trendLowPassEnabled,
      activeOverlaySessionNumbers,
      recalculatedSessions,
    ]
  );

  const segmentedTrendData = useTabAwareMemo(
    activeTab,
    ['hr'],
    () => {
      if (!snapshot || !isCnrCalabriaResearchUser) {
        return {
          pre: [] as SegmentFeatureRow[],
          post: [] as SegmentFeatureRow[],
        };
      }

      const sessionIds = snapshot.includedSessions.map(
        item => item.validation.sessionId
      );

      const buildRows = (segment: TrendSegmentKey): SegmentFeatureRow[] => {
        const perSession = sessionIds
          .map(sessionId => {
            const values = buildTrendSegmentValueMap(
              effectiveEventsBySessionId[sessionId] ?? [],
              event => getFeatureEventValue(event, selectedTrendFeature),
              segment,
              segmentWindowSeconds
            );

            return {
              sessionId,
              values: applyTrendProcessingToMap(
                values,
                trendNormalizationEnabled,
                trendLowPassEnabled
              ),
            };
          })
          .filter(entry => entry.values.size > 0);

        const maxSecond = perSession.reduce(
          (max, entry) =>
            Math.max(max, ...Array.from(entry.values.keys(), value => value)),
          -1
        );

        if (maxSecond < 0) return [];

        return Array.from({ length: maxSecond + 1 }, (_, second) => {
          const values = perSession
            .map(entry => entry.values.get(second))
            .filter((value): value is number => typeof value === 'number');

          const mean =
            values.length > 0
              ? values.reduce((sum, value) => sum + value, 0) / values.length
              : 0;
          const std =
            values.length > 1
              ? Math.sqrt(
                  values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
                    (values.length - 1)
                )
              : 0;

          const row: SegmentFeatureRow = {
            second,
            mean,
            stdUpper: mean + std,
            stdLower: mean - std,
          };

          activeOverlaySessionNumbers.forEach(sessionId => {
            const overlayValues = perSession.find(
              entry => entry.sessionId === sessionId
            );
            row[getOverlaySeriesKey(sessionId)] =
              overlayValues?.values.get(second) ?? null;
          });

          return row;
        });
      };

      return {
        pre: buildRows('pre'),
        post: buildRows('post'),
      };
    },
    [
      effectiveEventsBySessionId,
      activeOverlaySessionNumbers,
      isCnrCalabriaResearchUser,
      selectedTrendFeature,
      trendNormalizationEnabled,
      trendLowPassEnabled,
      snapshot,
      segmentWindowSeconds,
    ]
  );

  const cnrFullTrackTrendData = useTabAwareMemo(
    activeTab,
    ['hr'],
    () => {
      if (!snapshot || !isCnrCalabriaResearchUser) {
        return [] as SegmentFeatureRow[];
      }

      const sessionIds = snapshot.includedSessions.map(
        item => item.validation.sessionId
      );

      const perSession = sessionIds
        .map(sessionId => {
          const events = (effectiveEventsBySessionId[sessionId] ?? []).sort(
            (a, b) => a.timestamp - b.timestamp
          );

          if (events.length === 0) {
            return { sessionId, values: new Map<number, number>() };
          }

          const originTimestamp = getSessionTrackOriginTimestamp(events);
          if (originTimestamp == null) {
            return { sessionId, values: new Map<number, number>() };
          }

          const grouped = new Map<number, number[]>();

          events.forEach(event => {
            const value = getFeatureEventValue(event, selectedTrendFeature);
            if (typeof value !== 'number' || !Number.isFinite(value)) return;

            const second = eventToRelativeSecond(
              event.timestamp,
              originTimestamp
            );
            const bucket = grouped.get(second) ?? [];
            bucket.push(value);
            grouped.set(second, bucket);
          });

          const valueMap = new Map(
            [...grouped.entries()].map(([second, values]) => [
              second,
              values.reduce((sum, current) => sum + current, 0) / values.length,
            ])
          );

          return {
            sessionId,
            values: applyTrendProcessingToMap(
              valueMap,
              trendNormalizationEnabled,
              trendLowPassEnabled
            ),
          };
        })
        .filter(entry => entry.values.size > 0);

      const maxSecond = perSession.reduce(
        (max, entry) =>
          Math.max(max, ...Array.from(entry.values.keys(), value => value)),
        -1
      );

      if (maxSecond < 0) return [];

      return Array.from({ length: maxSecond + 1 }, (_, second) => {
        const values = perSession
          .map(entry => entry.values.get(second))
          .filter((value): value is number => typeof value === 'number');

        const mean =
          values.length > 0
            ? values.reduce((sum, value) => sum + value, 0) / values.length
            : 0;
        const std =
          values.length > 1
            ? Math.sqrt(
                values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
                  (values.length - 1)
              )
            : 0;

        const row: SegmentFeatureRow = {
          second,
          mean,
          stdUpper: mean + std,
          stdLower: mean - std,
        };

        activeOverlaySessionNumbers.forEach(sessionId => {
          const overlayValues = perSession.find(
            entry => entry.sessionId === sessionId
          );
          row[getOverlaySeriesKey(sessionId)] =
            overlayValues?.values.get(second) ?? null;
        });

        return row;
      });
    },
    [
      effectiveEventsBySessionId,
      activeOverlaySessionNumbers,
      isCnrCalabriaResearchUser,
      selectedTrendFeature,
      trendNormalizationEnabled,
      trendLowPassEnabled,
      snapshot,
    ]
  );

  const availableTrendSeconds = useMemo(
    () =>
      (isCnrCalabriaResearchUser ? cnrFullTrackTrendData : trendChartData)
        .map(point => Number(point.second))
        .filter(Number.isFinite),
    [cnrFullTrackTrendData, isCnrCalabriaResearchUser, trendChartData]
  );

  const trendStartOptions = useMemo(() => {
    if (availableTrendSeconds.length < 2) return [];
    return availableTrendSeconds.slice(0, -1).map(second => ({
      value: second.toString(),
      label: formatSecondLabel(second),
    }));
  }, [availableTrendSeconds]);

  const trendEndOptions = useMemo(() => {
    if (availableTrendSeconds.length < 2) return [];
    if (!trendStart) {
      return availableTrendSeconds.slice(1).map(second => ({
        value: second.toString(),
        label: formatSecondLabel(second),
      }));
    }

    const startSecond = Number(trendStart);
    return availableTrendSeconds
      .filter(second => second > startSecond)
      .map(second => ({
        value: second.toString(),
        label: formatSecondLabel(second),
      }));
  }, [availableTrendSeconds, trendStart]);

  const filteredTrendChartData = useMemo(() => {
    if (!trendStart && !trendEnd) return trendChartData;

    const startSecond = trendStart ? Number(trendStart) : -Infinity;
    const endSecond = trendEnd ? Number(trendEnd) : Infinity;

    return trendChartData.filter(point => {
      const second = Number(point.second);
      return second >= startSecond && second <= endSecond;
    });
  }, [trendChartData, trendStart, trendEnd]);
  const trendTimeTicks = useMemo(
    () => buildTrendTimeTicks(filteredTrendChartData),
    [filteredTrendChartData]
  );

  const buildSegmentSecondOptions = (rows: SegmentFeatureRow[]) =>
    rows.slice(0, -1).map(row => ({
      value: row.second.toString(),
      label: formatSecondLabel(row.second),
    }));

  const buildSegmentEndOptions = (
    rows: SegmentFeatureRow[],
    startValue: string | null
  ) => {
    const seconds = rows.map(row => row.second);
    if (seconds.length < 2) return [];
    if (!startValue) {
      return seconds.slice(1).map(second => ({
        value: second.toString(),
        label: formatSecondLabel(second),
      }));
    }
    const startSecond = Number(startValue);
    return seconds
      .filter(second => second > startSecond)
      .map(second => ({
        value: second.toString(),
        label: formatSecondLabel(second),
      }));
  };

  const filterSegmentRows = (
    rows: SegmentFeatureRow[],
    startValue: string | null,
    endValue: string | null
  ) => {
    if (!startValue && !endValue) return rows;
    const startSecond = startValue ? Number(startValue) : -Infinity;
    const endSecond = endValue ? Number(endValue) : Infinity;
    return rows.filter(
      row => row.second >= startSecond && row.second <= endSecond
    );
  };

  const preStartOptions = useMemo(
    () => buildSegmentSecondOptions(segmentedTrendData.pre),
    [segmentedTrendData.pre]
  );
  const preEndOptions = useMemo(
    () => buildSegmentEndOptions(segmentedTrendData.pre, preStart),
    [segmentedTrendData.pre, preStart]
  );
  const postStartOptions = useMemo(
    () => buildSegmentSecondOptions(segmentedTrendData.post),
    [segmentedTrendData.post]
  );
  const postEndOptions = useMemo(
    () => buildSegmentEndOptions(segmentedTrendData.post, postStart),
    [segmentedTrendData.post, postStart]
  );

  const filteredPreSegmentRows = useMemo(
    () => filterSegmentRows(segmentedTrendData.pre, preStart, preEnd),
    [segmentedTrendData.pre, preStart, preEnd]
  );
  const preSegmentTimeTicks = useMemo(
    () => buildTrendTimeTicks(filteredPreSegmentRows),
    [filteredPreSegmentRows]
  );
  const filteredFullTrackRows = useMemo(
    () => filterSegmentRows(cnrFullTrackTrendData, trendStart, trendEnd),
    [cnrFullTrackTrendData, trendStart, trendEnd]
  );
  const fullTrackTimeTicks = useMemo(
    () => buildTrendTimeTicks(filteredFullTrackRows),
    [filteredFullTrackRows]
  );
  const fullTrackSegmentOverlay = useMemo(() => {
    if (!cnrFullTrackTrendData.length) {
      return null;
    }

    const fullSeconds = cnrFullTrackTrendData.map(row => row.second);
    const maxSecond = Math.max(...fullSeconds);
    return {
      preStart: 0,
      preEnd: Math.min(segmentWindowSeconds - 1, maxSecond),
      postStart: Math.min(segmentWindowSeconds, maxSecond),
      postEnd: maxSecond,
    };
  }, [cnrFullTrackTrendData, segmentWindowSeconds]);
  const filteredPostSegmentRows = useMemo(
    () => filterSegmentRows(segmentedTrendData.post, postStart, postEnd),
    [segmentedTrendData.post, postStart, postEnd]
  );
  const postSegmentTimeTicks = useMemo(
    () => buildTrendTimeTicks(filteredPostSegmentRows),
    [filteredPostSegmentRows]
  );

  const descriptiveStatisticalValidation = useTabAwareMemo(
    activeTab,
    ['statistics'],
    (): Record<StatisticalSegmentKey, DescriptiveStatRow[]> => {
      const emptyStats: Record<StatisticalSegmentKey, DescriptiveStatRow[]> = {
        full: [],
        pre: [],
        session: [],
      };

      if (!snapshot) return emptyStats;

      const createFeatureBuckets = (): Record<
        FeatureKey,
        SessionFeatureMean[]
      > => ({
        HR: [],
        HRV: [],
        LF: [],
        HF: [],
      });

      const valuesBySegment: Record<
        StatisticalSegmentKey,
        Record<FeatureKey, SessionFeatureMean[]>
      > = {
        full: createFeatureBuckets(),
        pre: createFeatureBuckets(),
        session: createFeatureBuckets(),
      };

      const pushSessionMean = (
        segment: StatisticalSegmentKey,
        feature: FeatureKey,
        sessionId: number,
        patientId: string,
        values: number[]
      ) => {
        const mean = meanOf(values);
        if (mean == null) return;
        valuesBySegment[segment][feature].push({
          sessionId,
          patientId,
          value: mean,
        });
      };

      snapshot.includedSessions.forEach(item => {
        const sessionId = item.validation.sessionId;
        const patientId = item.validation.patientId;

        if (isCnrCalabriaResearchUser) {
          const events = [
            ...(effectiveEventsBySessionId[sessionId] ?? []),
          ].sort((left, right) => left.timestamp - right.timestamp);
          const originTimestamp = getSessionTrackOriginTimestamp(events);

          if (!originTimestamp) return;

          const segmentEvents: Record<StatisticalSegmentKey, HeartRateEvent[]> =
            {
              full: events,
              pre: [],
              session: [],
            };

          events.forEach(event => {
            const relativeSecond = eventToRelativeSecond(
              event.timestamp,
              originTimestamp
            );

            if (relativeSecond < segmentWindowSeconds) {
              segmentEvents.pre.push(event);
            } else {
              segmentEvents.session.push(event);
            }
          });

          featureKeys.forEach(feature => {
            (Object.keys(segmentEvents) as StatisticalSegmentKey[]).forEach(
              segment => {
                const values = segmentEvents[segment]
                  .map(event => getFeatureEventValue(event, feature))
                  .filter(
                    (value): value is number =>
                      typeof value === 'number' && Number.isFinite(value)
                  );
                pushSessionMean(segment, feature, sessionId, patientId, values);
              }
            );
          });

          return;
        }

        const validation = getEffectiveValidation(item);

        featureKeys.forEach(feature => {
          const baselineValues = validation.series.baseline.values[
            feature
          ].filter(
            (value): value is number =>
              typeof value === 'number' && Number.isFinite(value)
          );
          const exposureValues = validation.series.exposure.values[
            feature
          ].filter(
            (value): value is number =>
              typeof value === 'number' && Number.isFinite(value)
          );

          pushSessionMean('full', feature, sessionId, patientId, [
            ...baselineValues,
            ...exposureValues,
          ]);
          pushSessionMean('pre', feature, sessionId, patientId, baselineValues);
          pushSessionMean(
            'session',
            feature,
            sessionId,
            patientId,
            exposureValues
          );
        });
      });

      const buildRows = (
        segment: StatisticalSegmentKey
      ): DescriptiveStatRow[] =>
        featureKeys.map(feature => {
          const sessionValues = valuesBySegment[segment][feature];
          const values = sessionValues.map(item => item.value);
          const mean = meanOf(values);
          const std = sampleStdOf(values, mean);
          const coefficientOfVariation =
            mean != null && std != null && Math.abs(mean) > 0
              ? (std / Math.abs(mean)) * 100
              : null;
          const outlierSessions =
            mean == null
              ? []
              : sessionValues
                  .map(item => ({
                    ...item,
                    distance: Math.abs(item.value - mean),
                  }))
                  .sort((left, right) => right.distance - left.distance)
                  .slice(0, 3);

          return {
            feature,
            sessionCount: sessionValues.length,
            mean,
            std,
            min: values.length > 0 ? Math.min(...values) : null,
            max: values.length > 0 ? Math.max(...values) : null,
            coefficientOfVariation,
            outlierSessions,
          };
        });

      return {
        full: buildRows('full'),
        pre: buildRows('pre'),
        session: buildRows('session'),
      };
    },
    [
      effectiveEventsBySessionId,
      isCnrCalabriaResearchUser,
      recalculatedSessions,
      segmentWindowSeconds,
      snapshot,
    ]
  );

  const selectedPsdData = useMemo(() => {
    if (!snapshot) return { baseline: [], exposure: [] };

    const baselineSource = selectedSession
      ? selectedSession.spectral.psd.baseline
      : snapshot.groupSummary.aggregatedPsd.baseline;
    const exposureSource = selectedSession
      ? selectedSession.spectral.psd.exposure
      : snapshot.groupSummary.aggregatedPsd.exposure;

    const transform = (
      frequencies: number[],
      psdLf: number[],
      psdHf: number[]
    ) =>
      frequencies.map((frequency, index) => ({
        frequency,
        lf: logScale ? Math.log10(psdLf[index] + 1e-6) : psdLf[index],
        hf: logScale ? Math.log10(psdHf[index] + 1e-6) : psdHf[index],
      }));

    return {
      baseline: transform(
        baselineSource.frequencies,
        baselineSource.psd_lf,
        baselineSource.psd_hf
      ),
      exposure: transform(
        exposureSource.frequencies,
        exposureSource.psd_lf,
        exposureSource.psd_hf
      ),
    };
  }, [logScale, selectedSession, snapshot]);

  const availableSpectraFrequencies = useMemo(() => {
    const frequencies = selectedPsdData.baseline.map(point => point.frequency);
    return [...new Set(frequencies)].sort((a, b) => a - b);
  }, [selectedPsdData]);

  const formatFrequencyLabel = (frequency: number) =>
    `${frequency.toFixed(3)} Hz`;

  const spectraStartOptions = useMemo(() => {
    if (availableSpectraFrequencies.length < 2) return [];
    return availableSpectraFrequencies.slice(0, -1).map(frequency => ({
      value: frequency.toString(),
      label: formatFrequencyLabel(frequency),
    }));
  }, [availableSpectraFrequencies]);

  const spectraEndOptions = useMemo(() => {
    if (availableSpectraFrequencies.length < 2) return [];
    if (!spectraStart) {
      return availableSpectraFrequencies.slice(1).map(frequency => ({
        value: frequency.toString(),
        label: formatFrequencyLabel(frequency),
      }));
    }

    const startFrequency = Number(spectraStart);
    return availableSpectraFrequencies
      .filter(frequency => frequency > startFrequency)
      .map(frequency => ({
        value: frequency.toString(),
        label: formatFrequencyLabel(frequency),
      }));
  }, [availableSpectraFrequencies, spectraStart]);

  const filteredPsdData = useMemo(() => {
    if (!spectraStart && !spectraEnd) return selectedPsdData;

    const startFrequency = spectraStart ? Number(spectraStart) : -Infinity;
    const endFrequency = spectraEnd ? Number(spectraEnd) : Infinity;

    const filterRange = (
      points: Array<{ frequency: number; lf: number; hf: number }>
    ) =>
      points.filter(
        point =>
          point.frequency >= startFrequency && point.frequency <= endFrequency
      );

    return {
      baseline: filterRange(selectedPsdData.baseline),
      exposure: filterRange(selectedPsdData.exposure),
    };
  }, [selectedPsdData, spectraStart, spectraEnd]);

  const filteredSessions = useMemo(() => {
    if (!snapshot) return [];

    return snapshot.includedSessions.filter(item => {
      const outlierOk =
        outlierFilter === 'yes'
          ? item.spectral.outlier
          : outlierFilter === 'no'
            ? !item.spectral.outlier
            : true;

      return outlierOk;
    });
  }, [outlierFilter, snapshot]);

  const repairPanelSessions = useMemo(() => {
    if (!snapshot) return [];

    return snapshot.includedSessions.map(item => {
      const effectiveValidation = getEffectiveValidation(item);

      return {
        item,
        validation: effectiveValidation,
        excluded: getEffectiveExcluded(item) ?? null,
      };
    });
  }, [snapshot, recalculatedSessions]);

  const overviewEventMetrics = useMemo(
    () =>
      snapshot
        ? computeOverviewEventMetrics(
            snapshot,
            sessionAnalysisById,
            recalculatedSessions,
            sessionDataSourceById
          )
        : { perfectSessions: 0, sessionsWithEventIssues: 0 },
    [snapshot, sessionAnalysisById, recalculatedSessions, sessionDataSourceById]
  );
  const { perfectSessions, sessionsWithEventIssues } = overviewEventMetrics;

  const buildTrendRows = (feature: FeatureKey) => {
    if (!snapshot) return [];

    const buildCsvRow = (
      row: SegmentFeatureRow,
      view: string,
      segment: string
    ) => {
      const overlayColumns = Object.fromEntries(
        activeOverlaySessionNumbers.map(sessionId => [
          `Sessione ${sessionId}`,
          row[getOverlaySeriesKey(sessionId)] ?? '',
        ])
      );

      return {
        Vista: view,
        Segnale: feature,
        Secondo: row.second,
        Timeline: formatSecondLabel(row.second),
        Segmento: segment,
        Normalizzazione: trendNormalizationEnabled ? 'yes' : 'no',
        'Filtro passabasso': trendLowPassEnabled ? 'yes' : 'no',
        Modalita: [
          trendNormalizationEnabled ? 'normalizzato' : 'reale',
          trendLowPassEnabled ? 'filtrato' : 'non filtrato',
        ].join(' + '),
        Media: row.mean,
        'Std superiore': row.stdUpper,
        'Std inferiore': row.stdLower,
        ...overlayColumns,
      };
    };

    if (isCnrCalabriaResearchUser) {
      const sessionIds = snapshot.includedSessions.map(
        item => item.validation.sessionId
      );
      const buildSegmentRows = (
        segment: TrendSegmentKey | 'full'
      ): SegmentFeatureRow[] => {
        const perSession = sessionIds
          .map(sessionId => {
            if (segment === 'full') {
              const events = (effectiveEventsBySessionId[sessionId] ?? []).sort(
                (a, b) => a.timestamp - b.timestamp
              );
              if (events.length === 0) {
                return { sessionId, values: new Map<number, number>() };
              }

              const originTimestamp = getSessionTrackOriginTimestamp(events);
              if (originTimestamp == null) {
                return { sessionId, values: new Map<number, number>() };
              }

              const grouped = new Map<number, number[]>();
              events.forEach(event => {
                const value = getFeatureEventValue(event, feature);
                if (typeof value !== 'number' || !Number.isFinite(value)) {
                  return;
                }
                const second = eventToRelativeSecond(
                  event.timestamp,
                  originTimestamp
                );
                const bucket = grouped.get(second) ?? [];
                bucket.push(value);
                grouped.set(second, bucket);
              });

              const valueMap = new Map(
                [...grouped.entries()].map(([second, values]) => [
                  second,
                  values.reduce((sum, current) => sum + current, 0) /
                    values.length,
                ])
              );

              return {
                sessionId,
                values: applyTrendProcessingToMap(
                  valueMap,
                  trendNormalizationEnabled,
                  trendLowPassEnabled
                ),
              };
            }

            const values = buildTrendSegmentValueMap(
              effectiveEventsBySessionId[sessionId] ?? [],
              event => getFeatureEventValue(event, feature),
              segment,
              segmentWindowSeconds
            );

            return {
              sessionId,
              values: applyTrendProcessingToMap(
                values,
                trendNormalizationEnabled,
                trendLowPassEnabled
              ),
            };
          })
          .filter(entry => entry.values.size > 0);

        const maxSecond = perSession.reduce(
          (max, entry) =>
            Math.max(max, ...Array.from(entry.values.keys(), value => value)),
          -1
        );

        if (maxSecond < 0) return [];

        return Array.from({ length: maxSecond + 1 }, (_, second) => {
          const values = perSession
            .map(entry => entry.values.get(second))
            .filter((value): value is number => typeof value === 'number');

          if (values.length === 0) {
            return null;
          }

          const mean =
            values.reduce((sum, value) => sum + value, 0) / values.length;
          const std =
            values.length > 1
              ? Math.sqrt(
                  values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
                    (values.length - 1)
                )
              : 0;

          const row: SegmentFeatureRow = {
            second,
            mean,
            stdUpper: mean + std,
            stdLower: mean - std,
          };

          activeOverlaySessionNumbers.forEach(sessionId => {
            const overlayValues = perSession.find(
              entry => entry.sessionId === sessionId
            );
            row[getOverlaySeriesKey(sessionId)] =
              overlayValues?.values.get(second) ?? null;
          });

          return row;
        }).filter(Boolean) as SegmentFeatureRow[];
      };

      if (trendSegmentTab === 'full') {
        return [
          ...buildCsvMetadataRows('evoluzione-segnali'),
          ...filterSegmentRows(
            buildSegmentRows('full'),
            trendStart,
            trendEnd
          ).map(row => buildCsvRow(row, 'tratto-completo', 'tratto-completo')),
        ];
      }

      if (trendSegmentTab === 'pre') {
        return [
          ...buildCsvMetadataRows('evoluzione-segnali'),
          ...filterSegmentRows(buildSegmentRows('pre'), preStart, preEnd).map(
            row => buildCsvRow(row, 'pre-vr-baseline', 'pre-vr-baseline')
          ),
        ];
      }

      return [
        ...buildCsvMetadataRows('evoluzione-segnali'),
        ...filterSegmentRows(buildSegmentRows('post'), postStart, postEnd).map(
          row => buildCsvRow(row, 'sessione-vr', 'sessione-vr')
        ),
      ];
    }

    const validations = snapshot.includedSessions.map(item => {
      const validation = getEffectiveValidation(item);

      return {
        sessionId: validation.sessionId,
        baselineValues: applyTrendProcessingToSeries(
          validation.series.baseline.values[feature],
          trendNormalizationEnabled,
          trendLowPassEnabled
        ),
        exposureValues: applyTrendProcessingToSeries(
          validation.series.exposure.values[feature],
          trendNormalizationEnabled,
          trendLowPassEnabled
        ),
      };
    });

    return [
      ...buildCsvMetadataRows('evoluzione-segnali'),
      ...(Array.from({ length: 600 }, (_, second) => {
        const segment = second < 300 ? 'pre-vr' : 'post-vr';
        const index = second < 300 ? second : second - 300;
        const values = validations
          .map(validation =>
            second < 300
              ? validation.baselineValues[index]
              : validation.exposureValues[index]
          )
          .filter((value): value is number => typeof value === 'number');

        if (values.length === 0) return null;

        const mean =
          values.reduce((sum, value) => sum + value, 0) / values.length;
        const std =
          values.length > 1
            ? Math.sqrt(
                values.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
                  (values.length - 1)
              )
            : 0;

        const row: SegmentFeatureRow = {
          second,
          mean,
          stdUpper: mean + std,
          stdLower: mean - std,
        };

        activeOverlaySessionNumbers.forEach(sessionId => {
          const sourceValidation = validations.find(
            validation => validation.sessionId === sessionId
          );
          const source =
            segment === 'pre-vr'
              ? sourceValidation?.baselineValues
              : sourceValidation?.exposureValues;
          row[getOverlaySeriesKey(sessionId)] = source?.[index] ?? null;
        });

        return buildCsvRow(row, 'pre-post-vr', segment);
      })
        .filter(Boolean)
        .filter(row => {
          const startSecond = trendStart ? Number(trendStart) : -Infinity;
          const endSecond = trendEnd ? Number(trendEnd) : Infinity;
          const second = Number(row?.Secondo);
          return second >= startSecond && second <= endSecond;
        }) as Array<Record<string, string | number>>),
    ];
  };

  const overviewCsvData = snapshot
    ? [
        ...buildCsvMetadataRows('overview'),
        { Campo: 'Titolo', Valore: snapshot.name },
        { Campo: 'Descrizione', Valore: snapshot.description || '' },
        { Campo: 'Autore', Valore: snapshot.author },
        { Campo: 'Creata il', Valore: formatDate(snapshot.createdAt) },
        {
          Campo: 'Sessioni selezionate',
          Valore: snapshot.selectedSessions.length,
        },
        {
          Campo: 'Sessioni incluse',
          Valore: snapshot.groupSummary.includedSessions,
        },
        { Campo: 'Sessioni perfette', Valore: perfectSessions },
        {
          Campo: 'Sessioni con gap o duplicati',
          Valore: sessionsWithEventIssues,
        },
        {
          Campo: 'HR delta medio',
          Valore: snapshot.groupSummary.averageHrDelta,
        },
        { Campo: 'HR delta std', Valore: snapshot.groupSummary.hrDeltaStd },
        {
          Campo: 'LF/HF delta medio',
          Valore: snapshot.groupSummary.averageLfHfDelta,
        },
        {
          Campo: 'LF/HF delta std',
          Valore: snapshot.groupSummary.lfHfDeltaStd,
        },
        { Campo: 'Outlier', Valore: snapshot.groupSummary.outlierCount },
      ]
    : [];

  function isSessionRepaired(sessionId: number) {
    return (
      sessionDataSourceById[sessionId] === 'hrData_repaired' ||
      recalculatedSessions[sessionId]?.source === 'repaired'
    );
  }

  function isSessionRepairStateLoading(sessionId: number) {
    return effectiveValidationsLoading && !(sessionId in sessionDataSourceById);
  }

  const sessionsCsvData = snapshot
    ? [
        ...buildCsvMetadataRows('sessioni'),
        ...snapshot.includedSessions.map(item => {
          const sessionId = item.validation.sessionId;
          const analysis = sessionAnalysisById[sessionId];
          const isRepaired =
            sessionDataSourceById[sessionId] === 'hrData_repaired' ||
            recalculatedSessions[sessionId]?.source === 'repaired';
          const indicator = getSessionEventBadge({
            analysis,
            isRepaired,
            isExcluded: false,
            isLoading: isSessionRepairStateLoading(sessionId),
          });
          const prep = item.spectral.preprocessingSummary;
          const qc = hrvQcBySessionId[sessionId];
          return {
            'Session ID': sessionId,
            Utente: item.validation.patientId,
            Data: formatDate(item.validation.sessionDate),
            Eventi: indicator.label,
            'HR Δ': item.spectral.spectralMetrics.HR.delta,
            'LF/HF Δ': item.spectral.spectralMetrics.LFHF_ratio.delta,
            'RMSSD pre-VR (ms)':
              item.spectral.spectralMetrics.HRV.baseline.rmssd ?? '',
            'RMSSD post-VR (ms)':
              item.spectral.spectralMetrics.HRV.exposure.rmssd ?? '',
            correctedBeatPercent: usesHrvPreprocessing(prep)
              ? maxCorrectedBeatPercent(prep)
              : '',
            preprocessingVersion: prep.preprocessingVersion ?? '',
            nnSource: prep.nnSource ?? '',
            hrvQcReviewed: qc?.reviewed ? 'yes' : 'no',
            hrvQcNotes: qc?.notes ?? '',
          };
        }),
        ...snapshot.excludedSessions.map(item => ({
          'Session ID': item.sessionId,
          Utente: item.patientId,
          Data: formatDate(item.sessionDate),
          Eventi: `esclusa: ${item.reason}`,
          'HR Δ': '',
          'LF/HF Δ': '',
        })),
      ]
    : [];

  const trendHrCsvData = buildTrendRows('HR');
  const trendHrvCsvData = buildTrendRows('HRV');
  const trendLfCsvData = buildTrendRows('LF');
  const trendHfCsvData = buildTrendRows('HF');

  const spectraCsvData = snapshot
    ? [
        ...buildCsvMetadataRows('spettri'),
        ...selectedPsdData.baseline.map(point => ({
          Segmento: 'pre-VR',
          Frequenza: point.frequency,
          LF: point.lf,
          HF: point.hf,
        })),
        ...selectedPsdData.exposure.map(point => ({
          Segmento: 'post-VR',
          Frequenza: point.frequency,
          LF: point.lf,
          HF: point.hf,
        })),
      ]
    : [];

  const handleSessionHydrationPatch = useCallback(
    (patch: SessionHydrationPatch) => {
      setSessionDataSourceById(current => ({
        ...current,
        [patch.sessionId]: patch.dataSource,
      }));
      setEffectiveEventsBySessionId(current => ({
        ...current,
        [patch.sessionId]: patch.effectiveEvents,
      }));

      if (patch.recalculated) {
        setRecalculatedSessions(current => ({
          ...current,
          [patch.sessionId]: patch.recalculated!,
        }));
      }

      if (patch.repairPreview) {
        setRepairPreviews(current => ({
          ...current,
          [patch.sessionId]: patch.repairPreview!,
        }));
      } else {
        setRepairPreviews(current => {
          const next = { ...current };
          delete next[patch.sessionId];
          return next;
        });
      }
    },
    []
  );

  const handleHydrationLoadingChange = useCallback((isLoading: boolean) => {
    setEffectiveValidationsLoading(isLoading);
  }, []);

  const handleSeedSessionAnalysis = useCallback(
    (
      entries: Record<number, Exclude<SessionAnalysisState, { status: 'idle' }>>
    ) => {
      setSessionAnalysisById(current => ({ ...current, ...entries }));
    },
    []
  );

  const hydrationEnabled = activeTab === 'sessions' || activeTab === 'hr';

  useEffectiveSessionHydration({
    snapshot,
    enabled: hydrationEnabled,
    prioritySessionId: selectedSessionId ? Number(selectedSessionId) : null,
    onLoadingChange: handleHydrationLoadingChange,
    onSessionPatch: handleSessionHydrationPatch,
    onSeedSessionAnalysis: handleSeedSessionAnalysis,
  });

  const openRepairPreview = (sessionId: number) => {
    if (!repairPreviews[sessionId]) return;
    setRepairPreviewView('list');
    setRepairPreviewSessionId(sessionId);
  };

  const runSessionAnalysis = async (sessionId: number) => {
    const cachedResult = getCachedSessionHrDataAnalysis(sessionId);
    if (cachedResult) {
      setSessionAnalysisById(current => ({
        ...current,
        [sessionId]: mapAnalysisResultToState(cachedResult),
      }));
      return;
    }

    setAnalyzingSessionId(sessionId);
    try {
      const result = await analyzeSessionHrData(sessionId);
      const nextState = mapAnalysisResultToState(result);

      setSessionAnalysisById(current => ({
        ...current,
        [sessionId]: nextState,
      }));

      if (nextState.status === 'failed') {
        notifications.show({
          color: 'red',
          title: 'Analisi fallita',
          message: getAnalysisServiceErrorMessage(new Error(nextState.message)),
        });
        return;
      }

      if (nextState.status === 'clean') {
        notifications.show({
          color: 'teal',
          title: 'Analisi completata',
          message: nextState.summary ?? 'Record perfetto.',
        });
        return;
      }

      if (nextState.status === 'needs_repair') {
        notifications.show({
          color: 'orange',
          title: 'Analisi completata',
          message: nextState.summary ?? 'Sessione da riparare.',
        });
      }
    } catch (error) {
      const message = getAnalysisServiceErrorMessage(
        error instanceof Error ? error : new Error('Analisi non riuscita.')
      );
      setSessionAnalysisById(current => ({
        ...current,
        [sessionId]: { status: 'failed', message },
      }));
      notifications.show({
        color: 'red',
        title: 'Analisi fallita',
        message,
      });
    } finally {
      setAnalyzingSessionId(null);
    }
  };

  const runEventRepair = async (sessionId: number) => {
    if (sessionAnalysisById[sessionId]?.status !== 'needs_repair') {
      return;
    }

    if (!isOpenAiRepairConfigured()) {
      notifications.show({
        color: 'yellow',
        title: 'Riparazione non disponibile',
        message:
          'OpenAI non configurato. Imposta VITE_OPENAI_API_KEY in .env.local e riavvia il dev server.',
      });
      return;
    }

    setRepairingSessionId(sessionId);
    try {
      const result = await repairSessionHrDataWithAi(sessionId);

      if (result.status === 'no-op') {
        setRepairPreviews(current => {
          const next = { ...current };
          delete next[sessionId];
          return next;
        });
        setRecalculatedSessions(current => {
          const next = { ...current };
          delete next[sessionId];
          return next;
        });
        notifications.show({
          color: 'yellow',
          title: 'Nessuna riparazione necessaria',
          message:
            result.message ||
            'Non sono stati trovati gap o duplicati riparabili.',
        });
        return;
      }

      if (result.status === 'failed' || !result.repairedEvents) {
        const failedChecks =
          result.validation?.deterministic.checks
            .filter(check => !check.passed)
            .map(check => check.message) ?? [];
        notifications.show({
          color: 'red',
          title: 'Riparazione eventi rifiutata',
          message: [
            result.message ?? 'Validazione fallita.',
            ...failedChecks.slice(0, 2),
            ...(result.validation?.ai?.findings.slice(0, 2) ?? []),
          ]
            .filter(Boolean)
            .join(' '),
        });
        return;
      }

      const repairedEvents = result.repairedEvents;

      invalidateSessionHrDataAnalysisCache(sessionId);

      setRepairPreviews(current => ({
        ...current,
        [sessionId]: {
          repairedOnly: result.repairedOnly ?? [],
          repairedEvents,
          originalCount: result.originalCount,
          repairedCount: result.repairedCount ?? repairedEvents.length,
        },
      }));
      setSessionDataSourceById(current => ({
        ...current,
        [sessionId]: 'hrData_repaired',
      }));
      setEffectiveEventsBySessionId(current => ({
        ...current,
        [sessionId]: repairedEvents,
      }));
      setSessionAnalysisById(current => {
        const next = { ...current };
        delete next[sessionId];
        return next;
      });

      notifications.show({
        color: 'teal',
        title: 'Riparazione completata',
        message: `Sessione ${sessionId}: salvato hrData_repaired con ${result.repairedCount} eventi e ${result.repairedOnly?.length ?? 0} correzioni (${result.duplicateMerges ?? 0} duplicati, ${result.gapFillCount ?? 0} gap).`,
      });

      void (async () => {
        try {
          const configuration = await getConfiguration(sessionId);
          const pipeline = runSpectralAnalysis([
            {
              ...configuration.data,
              hrData_repaired: repairedEvents,
            },
          ]);
          setRecalculatedSessions(current => ({
            ...current,
            [sessionId]: pipeline.validations[0]
              ? {
                  source: 'repaired',
                  validation: pipeline.validations[0],
                }
              : {
                  source: 'repaired',
                  excluded: pipeline.excludedSessions[0] ?? {
                    sessionId,
                    patientId: 'n/a',
                    sessionDate: new Date().toISOString(),
                    reason: 'Sessione esclusa dopo ricalcolo',
                  },
                },
          }));
        } catch (recalcError) {
          notifications.show({
            color: 'yellow',
            title: 'Ricalcolo locale non riuscito',
            message:
              recalcError instanceof Error
                ? recalcError.message
                : 'I dati riparati sono salvati, ma il ricalcolo grafico è fallito.',
          });
        }
      })();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Riparazione eventi fallita',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile riparare la sessione.',
      });
    } finally {
      setRepairingSessionId(null);
    }
  };

  const handleExportCsv = () => {
    if (!snapshot) return;

    if (exportSelectedSections.length === 0) {
      notifications.show({
        color: 'yellow',
        title: 'Attenzione',
        message: 'Seleziona almeno una sezione da esportare.',
      });
      return;
    }

    setIsExporting(true);

    const refs = [
      { ref: overviewCsvRef, section: 'overview' },
      { ref: sessionsCsvRef, section: 'sessions' },
      { ref: trendHrCsvRef, section: 'trend-hr' },
      { ref: trendHrvCsvRef, section: 'trend-hrv' },
      { ref: trendLfCsvRef, section: 'trend-lf' },
      { ref: trendHfCsvRef, section: 'trend-hf' },
      { ref: spectraCsvRef, section: 'spectra' },
    ];

    let delay = 0;
    refs.forEach(({ ref, section }) => {
      if (exportSelectedSections.includes(section)) {
        setTimeout(() => {
          ref.current?.link?.click();
        }, delay);
        delay += 200;
      }
    });

    setTimeout(() => {
      setIsExporting(false);
      closeExportModal();
      notifications.show({
        color: 'green',
        title: 'Esportazione completata',
        message: `Esportate ${exportSelectedSections.length} sezioni.`,
      });
    }, 500);
  };

  const resetEditSessionsModal = () => {
    setEditSearchId('');
    setEditSearchPatient('');
    setEditSearchDate('');
    setEditPage(1);
  };

  const openEditSessions = () => {
    if (!snapshot) return;
    setEditSelectedSessionIds(snapshot.selectedSessions);
    resetEditSessionsModal();
    openEditSessionsModal();
  };

  const closeEditSessions = () => {
    if (isUpdatingSessions) return;
    resetEditSessionsModal();
    closeEditSessionsModal();
  };

  const toggleEditSession = (sessionId: number) => {
    setEditSelectedSessionIds(current =>
      current.includes(sessionId)
        ? current.filter(id => id !== sessionId)
        : [...current, sessionId]
    );
  };

  const handleUpdateAnalysisSessions = async () => {
    if (!snapshot) return;

    if (editSelectedSessionIds.length < 3) {
      notifications.show({
        color: 'red',
        title: 'Sessioni mancanti',
        message: 'Seleziona almeno 3 sessioni completed.',
      });
      return;
    }

    setIsUpdatingSessions(true);

    try {
      const fullSessions = await Promise.all(
        editSelectedSessionIds.map(async sessionId => {
          const response = await getConfiguration(sessionId);
          return response.data;
        })
      );

      const rebuiltSnapshot = buildSpectralAnalysisSnapshot(
        {
          name: snapshot.name,
          description: snapshot.description,
          sessionIds: editSelectedSessionIds,
        },
        fullSessions,
        snapshot.author
      );
      const nextSnapshot: SpectralAnalysisSnapshot = {
        ...rebuiltSnapshot,
        analysisId: snapshot.analysisId,
        author: snapshot.author,
        createdAt: snapshot.createdAt,
        verification: {
          status: 'not-run',
          notes: ['Verifica non ancora eseguita'],
        },
      };
      const persistedSnapshot = await spectralAnalysisStore.updateAnalysis(
        snapshot.analysisId,
        nextSnapshot
      );

      setSnapshot(persistedSnapshot);
      setSelectedSessionId(
        persistedSnapshot.includedSessions[0]?.validation.sessionId.toString() ??
          null
      );
      setSelectedOverlaySessionIds([]);
      setRepairPreviews({});
      setRecalculatedSessions({});
      setSessionAnalysisById({});
      setEffectiveEventsBySessionId({});
      setSessionDataSourceById({});
      setHrvQcBySessionId({});
      closeEditSessionsModal();

      notifications.show({
        color: 'green',
        title: 'Analisi aggiornata e ricalcolata',
        message: `${persistedSnapshot.groupSummary.includedSessions}/${persistedSnapshot.selectedSessions.length} sessioni incluse dopo il ricalcolo.`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Aggiornamento analisi fallito',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile aggiornare le sessioni dell’analisi.',
      });
    } finally {
      setIsUpdatingSessions(false);
    }
  };

  if (loading) {
    return (
      <Group justify='center' py='xl'>
        <Text>Caricamento analisi...</Text>
      </Group>
    );
  }

  if (!snapshot) {
    if (loading) {
      return (
        <div>
          <PageHeader
            title='Caricamento analisi…'
            subtitle='Normalizzazione spettrale su sessioni interne'
            breadcrumbs={[
              { label: 'Ricerca', href: '/research' },
              {
                label: 'Normalizzazione spettrale',
                href: '/research/spectral-normalization/legacy',
              },
            ]}
            backUrl='/research/spectral-normalization/legacy'
          />
          <Divider my='md' />
          <Tabs value='overview'>
            <Tabs.List>
              <Tabs.Tab value='overview'>Overview</Tabs.Tab>
              <Tabs.Tab value='hr'>Evoluzione segnali</Tabs.Tab>
              <Tabs.Tab value='spectra'>Spettri</Tabs.Tab>
              <Tabs.Tab value='statistics'>Validazione statistica</Tabs.Tab>
              <Tabs.Tab value='sessions'>Sessioni</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value='overview' pt='md'>
              <Stack gap='md'>
                <Skeleton height={72} />
                <Grid>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Grid.Col key={index} span={{ base: 12, md: 6 }}>
                      <Skeleton height={140} />
                    </Grid.Col>
                  ))}
                </Grid>
                <Skeleton height={280} />
              </Stack>
            </Tabs.Panel>
          </Tabs>
        </div>
      );
    }

    return (
      <Stack gap='md'>
        <PageHeader
          title='Analisi non trovata'
          breadcrumbs={[
            { label: 'Ricerca', href: '/research' },
            {
              label: 'Normalizzazione spettrale',
              href: '/research/spectral-normalization/legacy',
            },
          ]}
          backUrl='/research/spectral-normalization/legacy'
        />
        <Card withBorder>
          <Text c='dimmed'>
            L'analisi richiesta non e disponibile oppure non sei autorizzato a
            visualizzarla.
          </Text>
          <Button
            mt='md'
            onClick={() => navigate('/research/spectral-normalization/legacy')}
          >
            Torna alla lista
          </Button>
        </Card>
      </Stack>
    );
  }

  const statisticalValidation =
    snapshot.groupSummary.statisticalValidation ?? [];

  return (
    <div>
      <PageHeader
        title={snapshot.name}
        subtitle={
          snapshot.description ||
          'Normalizzazione spettrale su sessioni interne'
        }
        breadcrumbs={[
          { label: 'Ricerca', href: '/research' },
          {
            label: 'Normalizzazione spettrale',
            href: '/research/spectral-normalization/legacy',
          },
          { label: snapshot.name },
        ]}
        backUrl='/research/spectral-normalization/legacy'
        actions={
          <Group gap='xs'>
            <Button
              variant='light'
              onClick={() =>
                navigate('/research/spectral-normalization/documentation')
              }
            >
              Documentazione
            </Button>
            <Button variant='light' onClick={openEditSessions}>
              Modifica sessioni
            </Button>
            <Button
              variant='light'
              leftSection={<Download size={16} />}
              onClick={openExportModal}
            >
              Esporta CSV
            </Button>
          </Group>
        }
      />

      <Divider my='md' />

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab value='overview'>Overview</Tabs.Tab>
          <Tabs.Tab value='hr'>Evoluzione segnali</Tabs.Tab>
          <Tabs.Tab value='spectra'>Spettri</Tabs.Tab>
          <Tabs.Tab value='statistics'>Validazione statistica</Tabs.Tab>
          <Tabs.Tab value='sessions'>Sessioni</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='overview' pt='md' keepMounted>
          <Stack gap='md'>
            <SpectralOverviewTab
              snapshot={snapshot}
              sessionAnalysisById={sessionAnalysisById}
              recalculatedSessions={recalculatedSessions}
              sessionDataSourceById={sessionDataSourceById}
              effectiveValidationsLoading={effectiveValidationsLoading}
              formatDate={formatDate}
            />

            <Card withBorder>
              <Title order={4} mb='xs'>
                Dettaglio gap e duplicati
              </Title>
              <Text size='sm' c='dimmed' mb='md'>
                Analizza ogni sessione per rilevare duplicati nello stesso
                secondo e gap nei dati originali. Se necessario, usa Ripara
                eventi per unire i duplicati (media) e colmare i gap
                (interpolazione).
              </Text>

              {repairPanelSessions.length === 0 ? (
                <Text c='dimmed'>Nessuna sessione inclusa.</Text>
              ) : (
                <Stack gap='sm'>
                  {repairPanelSessions.map(({ item, validation, excluded }) => {
                    const sessionId = item.validation.sessionId;
                    const analysis = sessionAnalysisById[sessionId];
                    const isRepaired = isSessionRepaired(sessionId);
                    const isLoadingState =
                      isSessionRepairStateLoading(sessionId);
                    const isAnalyzing = analyzingSessionId === sessionId;
                    const isRepairing = repairingSessionId === sessionId;
                    const eventBadge = getSessionEventBadge({
                      analysis,
                      isRepaired,
                      isExcluded: Boolean(excluded),
                      isLoading: isLoadingState,
                    });
                    const analysisFindings =
                      analysis?.status === 'clean' ||
                      analysis?.status === 'needs_repair'
                        ? (analysis.findings ?? [])
                        : [];
                    const detailLines =
                      analysisFindings.length > 0
                        ? analysisFindings
                        : analysis?.status === 'failed'
                          ? [analysis.message]
                          : [];
                    const sessionDate =
                      (validation ?? excluded)?.sessionDate ??
                      item.validation.sessionDate;
                    const sessionDurationLabel = formatSessionEventsDuration(
                      effectiveEventsBySessionId[sessionId]
                    );

                    return (
                      <Card key={`quality-issue-${sessionId}`} withBorder>
                        <Group justify='space-between' align='flex-start'>
                          <div>
                            <Group gap='xs' mb={4}>
                              <Title order={5}>Sessione {sessionId}</Title>
                              <Badge variant='light'>
                                {item.validation.patientId}
                              </Badge>
                              {excluded ? (
                                <Badge color='gray' variant='outline'>
                                  ESCLUSA DOPO RICALCOLO
                                </Badge>
                              ) : (
                                <Badge
                                  color={eventBadge.color}
                                  variant={eventBadge.variant}
                                >
                                  {eventBadge.label}
                                </Badge>
                              )}
                            </Group>
                            <Text size='sm' c='dimmed'>
                              {formatDate(sessionDate)} | Durata:{' '}
                              {sessionDurationLabel}
                            </Text>
                            {analysis?.status === 'needs_repair' && (
                              <Text size='sm' c='dimmed' mt='xs'>
                                {analysis.duplicateCount} duplicati |{' '}
                                {analysis.gapCount} gap
                              </Text>
                            )}
                            {isLoadingState ? (
                              <Stack gap={6} mt='xs'>
                                <Skeleton height={12} radius='sm' width='72%' />
                                <Skeleton height={12} radius='sm' width='56%' />
                              </Stack>
                            ) : detailLines.length > 0 ? (
                              <Stack gap={4} mt='xs'>
                                {detailLines.map(issue => (
                                  <Text key={issue} size='sm'>
                                    {issue}
                                  </Text>
                                ))}
                              </Stack>
                            ) : (
                              <Text size='sm' c='dimmed' mt='xs'>
                                Esegui l&apos;analisi per verificare duplicati e
                                gap sui dati originali.
                              </Text>
                            )}
                          </div>
                          <Group gap='xs'>
                            <Button
                              component='a'
                              href={`/sessions/completed/${sessionId}`}
                              target='_blank'
                              rel='noreferrer'
                              variant='subtle'
                              size='compact-sm'
                            >
                              Apri sessione
                            </Button>
                            {!isRepaired &&
                              (isLoadingState ? (
                                <Skeleton height={30} radius='md' width={126} />
                              ) : (
                                <Button
                                  variant='subtle'
                                  size='compact-sm'
                                  loading={isAnalyzing}
                                  disabled={
                                    isRepairing || analysis?.status === 'clean'
                                  }
                                  onClick={() =>
                                    void runSessionAnalysis(sessionId)
                                  }
                                >
                                  {isAnalyzing
                                    ? 'Analisi in corso'
                                    : 'Analizza sessione'}
                                </Button>
                              ))}
                            {!isRepaired &&
                              analysis?.status === 'needs_repair' && (
                                <Button
                                  variant='filled'
                                  size='compact-sm'
                                  leftSection={<Wrench size={14} />}
                                  loading={isRepairing}
                                  disabled={isLoadingState || isAnalyzing}
                                  onClick={() => void runEventRepair(sessionId)}
                                >
                                  {isRepairing
                                    ? 'Riparazione in corso'
                                    : 'Ripara eventi'}
                                </Button>
                              )}
                            {repairPreviews[sessionId] && (
                              <Button
                                variant='subtle'
                                size='compact-sm'
                                onClick={() => openRepairPreview(sessionId)}
                              >
                                Vedi riparazioni
                              </Button>
                            )}
                          </Group>
                        </Group>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Card>

            <Modal
              opened={repairPreviewSessionId !== null}
              onClose={() => setRepairPreviewSessionId(null)}
              title={
                repairPreviewSessionId
                  ? `Riparazioni sessione ${repairPreviewSessionId}`
                  : 'Riparazioni'
              }
              size='90%'
              centered
            >
              {repairPreviewSessionId &&
              repairPreviews[repairPreviewSessionId] ? (
                <Stack gap='md'>
                  <Text size='sm' c='dimmed'>
                    Eventi originali:{' '}
                    {repairPreviews[repairPreviewSessionId].originalCount} |
                    Eventi finali dopo riparazione:{' '}
                    {repairPreviews[repairPreviewSessionId].repairedCount}
                  </Text>
                  <Tabs defaultValue='repairs'>
                    <Tabs.List>
                      <Tabs.Tab value='repairs'>
                        Riparazioni effettuate
                      </Tabs.Tab>
                      <Tabs.Tab value='final-records'>Record riparato</Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value='repairs' pt='md'>
                      <ScrollArea h='70vh' type='auto'>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Timestamp</Table.Th>
                              <Table.Th>Tipo</Table.Th>
                              <Table.Th>Dettaglio</Table.Th>
                              <Table.Th>HR</Table.Th>
                              <Table.Th>HRV</Table.Th>
                              <Table.Th>LF</Table.Th>
                              <Table.Th>HF</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {repairPreviews[
                              repairPreviewSessionId
                            ].repairedOnly.map(event => (
                              <Table.Tr
                                key={`${event.timestamp}-${event.repair?.type}`}
                              >
                                <Table.Td>
                                  {formatTimestampToDateTimeWithMs(
                                    event.timestamp
                                  )}
                                </Table.Td>
                                <Table.Td>{event.repair?.type}</Table.Td>
                                <Table.Td>{event.repair?.summary}</Table.Td>
                                <Table.Td>
                                  {formatRepairMetric(event.hr)}
                                </Table.Td>
                                <Table.Td>
                                  {formatRepairMetric(event.hrv)}
                                </Table.Td>
                                <Table.Td>
                                  {formatRepairMetric(event.lf)}
                                </Table.Td>
                                <Table.Td>
                                  {formatRepairMetric(event.hf)}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    </Tabs.Panel>

                    <Tabs.Panel value='final-records' pt='md'>
                      {(() => {
                        const preview = repairPreviews[repairPreviewSessionId];
                        const repairedRecordsCount =
                          preview.repairedEvents.filter(event =>
                            Boolean(event.repair || event.type === 'gap')
                          ).length;
                        const gapAddedCount = preview.repairedEvents.filter(
                          event =>
                            event.type === 'gap' || event.repair?.type === 'gap'
                        ).length;
                        const repairedPercent =
                          preview.repairedCount > 0
                            ? (repairedRecordsCount / preview.repairedCount) *
                              100
                            : 0;
                        const duplicateAdjustedCount =
                          repairedRecordsCount - gapAddedCount;

                        return (
                          <Stack gap='sm' mb='sm'>
                            <Group justify='space-between' align='flex-start'>
                              <Text size='sm' c='dimmed' style={{ flex: 1 }}>
                                Eventi originali:{' '}
                                <Text span c='dark' fw={600}>
                                  {preview.originalCount}
                                </Text>{' '}
                                | Eventi finali dopo riparazione:{' '}
                                <Text span c='dark' fw={600}>
                                  {preview.repairedCount}
                                </Text>{' '}
                                | Eventi riparati nel record finale:{' '}
                                <Text span c='green' fw={700}>
                                  {repairedRecordsCount} (
                                  {formatRepairPercent(repairedPercent)})
                                </Text>
                                .{' '}
                                {gapAddedCount > 0 ? (
                                  <>
                                    Sono stati aggiunti{' '}
                                    <Text span c='green' fw={600}>
                                      {gapAddedCount}
                                    </Text>{' '}
                                    eventi per colmare i gap
                                    {duplicateAdjustedCount > 0
                                      ? ` e consolidati ${duplicateAdjustedCount} record duplicati.`
                                      : '.'}
                                  </>
                                ) : duplicateAdjustedCount > 0 ? (
                                  <>
                                    Sono stati consolidati{' '}
                                    <Text span c='green' fw={600}>
                                      {duplicateAdjustedCount}
                                    </Text>{' '}
                                    record duplicati.
                                  </>
                                ) : (
                                  ' Non sono stati applicati inserimenti o consolidamenti.'
                                )}{' '}
                                I record evidenziati in verde sono quelli
                                riparati o inseriti durante la correzione.
                              </Text>
                            </Group>
                            <Group justify='flex-end'>
                              <SegmentedControl
                                value={repairPreviewView}
                                onChange={value =>
                                  setRepairPreviewView(
                                    value as 'graph' | 'list'
                                  )
                                }
                                data={[
                                  {
                                    value: 'graph',
                                    label: (
                                      <Group gap={6} wrap='nowrap'>
                                        <ChartLineIcon size={14} />
                                        <Text size='sm' fw={500}>
                                          Graph
                                        </Text>
                                      </Group>
                                    ),
                                  },
                                  {
                                    value: 'list',
                                    label: (
                                      <Group gap={6} wrap='nowrap'>
                                        <ListIcon size={14} />
                                        <Text size='sm' fw={500}>
                                          List
                                        </Text>
                                      </Group>
                                    ),
                                  },
                                ]}
                              />
                            </Group>
                          </Stack>
                        );
                      })()}
                      {repairPreviewView === 'graph' ? (
                        <Card withBorder padding='sm'>
                          <ResponsiveContainer width='100%' height={360}>
                            <LineChart
                              data={repairPreviews[
                                repairPreviewSessionId
                              ].repairedEvents.map(event => ({
                                timestamp: event.timestamp,
                                label: formatTimestampToTimeWithMs(
                                  event.timestamp
                                ),
                                hr: event.hr,
                                hrv: event.hrv ?? null,
                                lf: event.lf ?? null,
                                hf: event.hf ?? null,
                              }))}
                            >
                              <CartesianGrid strokeDasharray='3 3' />
                              <XAxis
                                dataKey='label'
                                minTickGap={32}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip
                                formatter={(value, name) => [
                                  formatRepairMetric(
                                    typeof value === 'number'
                                      ? value
                                      : undefined
                                  ),
                                  String(name).toUpperCase(),
                                ]}
                                labelFormatter={(_, payload) => {
                                  const timestamp =
                                    payload?.[0]?.payload?.timestamp;

                                  if (
                                    typeof timestamp === 'number' &&
                                    Number.isFinite(timestamp)
                                  ) {
                                    return `Timestamp ${formatTimestampToDateTimeWithMs(
                                      timestamp
                                    )}`;
                                  }

                                  return 'Timestamp non disponibile';
                                }}
                              />
                              <Legend />
                              <Line
                                type='monotone'
                                dataKey='hr'
                                name='HR'
                                stroke='#228be6'
                                dot={false}
                                strokeWidth={2}
                              />
                              <Line
                                type='monotone'
                                dataKey='hrv'
                                name='HRV'
                                stroke='#12b886'
                                dot={false}
                                strokeWidth={2}
                              />
                              <Line
                                type='monotone'
                                dataKey='lf'
                                name='LF'
                                stroke='#f59f00'
                                dot={false}
                                strokeWidth={2}
                              />
                              <Line
                                type='monotone'
                                dataKey='hf'
                                name='HF'
                                stroke='#fa5252'
                                dot={false}
                                strokeWidth={2}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Card>
                      ) : (
                        <ScrollArea h='70vh' type='auto'>
                          <Table striped highlightOnHover>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Timestamp</Table.Th>
                                <Table.Th>Stato</Table.Th>
                                <Table.Th>HR</Table.Th>
                                <Table.Th>HRV</Table.Th>
                                <Table.Th>LF</Table.Th>
                                <Table.Th>HF</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {repairPreviews[
                                repairPreviewSessionId
                              ].repairedEvents.map(event => {
                                const isRepairedRecord = Boolean(
                                  event.repair || event.type === 'gap'
                                );

                                return (
                                  <Table.Tr
                                    key={`${event.timestamp}-${event.hr}-${event.hrv ?? 'na'}-${event.lf ?? 'na'}-${event.hf ?? 'na'}`}
                                    style={
                                      isRepairedRecord
                                        ? {
                                            backgroundColor:
                                              'var(--mantine-color-green-0)',
                                          }
                                        : undefined
                                    }
                                  >
                                    <Table.Td>
                                      {formatTimestampToDateTimeWithMs(
                                        event.timestamp
                                      )}
                                    </Table.Td>
                                    <Table.Td>
                                      {isRepairedRecord ? (
                                        <Badge color='green' variant='light'>
                                          Riparato
                                        </Badge>
                                      ) : (
                                        <Badge color='gray' variant='light'>
                                          Originale
                                        </Badge>
                                      )}
                                    </Table.Td>
                                    <Table.Td>
                                      {formatRepairMetric(event.hr)}
                                    </Table.Td>
                                    <Table.Td>
                                      {formatRepairMetric(event.hrv)}
                                    </Table.Td>
                                    <Table.Td>
                                      {formatRepairMetric(event.lf)}
                                    </Table.Td>
                                    <Table.Td>
                                      {formatRepairMetric(event.hf)}
                                    </Table.Td>
                                  </Table.Tr>
                                );
                              })}
                            </Table.Tbody>
                          </Table>
                        </ScrollArea>
                      )}
                    </Tabs.Panel>
                  </Tabs>
                </Stack>
              ) : (
                <Text c='dimmed'>Nessuna riparazione disponibile.</Text>
              )}
            </Modal>

            <Modal
              opened={exportOpened}
              onClose={closeExportModal}
              title='Esporta analisi'
              centered
              size='md'
            >
              <Stack gap='md'>
                <Text size='sm' c='dimmed'>
                  Seleziona le sezioni da esportare. Ogni sezione verrà
                  esportata in un file CSV separato. Per le evoluzioni segnali,
                  il CSV replica la vista corrente del grafico con{' '}
                  normalizzazione{' '}
                  {trendNormalizationEnabled ? 'attiva' : 'disattiva'} e filtro
                  passabasso {trendLowPassEnabled ? 'attivo' : 'disattivo'}.
                </Text>

                <Checkbox
                  label='Overview'
                  checked={exportSelectedSections.includes('overview')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'overview']
                        : current.filter(section => section !== 'overview')
                    )
                  }
                />
                <Checkbox
                  label='Lista sessioni selezionate'
                  checked={exportSelectedSections.includes('sessions')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'sessions']
                        : current.filter(section => section !== 'sessions')
                    )
                  }
                />
                <Checkbox
                  label='Evoluzione HR'
                  checked={exportSelectedSections.includes('trend-hr')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'trend-hr']
                        : current.filter(section => section !== 'trend-hr')
                    )
                  }
                />
                <Checkbox
                  label='Evoluzione HRV'
                  checked={exportSelectedSections.includes('trend-hrv')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'trend-hrv']
                        : current.filter(section => section !== 'trend-hrv')
                    )
                  }
                />
                <Checkbox
                  label='Evoluzione LF'
                  checked={exportSelectedSections.includes('trend-lf')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'trend-lf']
                        : current.filter(section => section !== 'trend-lf')
                    )
                  }
                />
                <Checkbox
                  label='Evoluzione HF'
                  checked={exportSelectedSections.includes('trend-hf')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'trend-hf']
                        : current.filter(section => section !== 'trend-hf')
                    )
                  }
                />
                <Checkbox
                  label='PSD tachogramma NN'
                  checked={exportSelectedSections.includes('spectra')}
                  onChange={e =>
                    setExportSelectedSections(current =>
                      e.currentTarget.checked
                        ? [...current, 'spectra']
                        : current.filter(section => section !== 'spectra')
                    )
                  }
                />

                <Group justify='flex-end'>
                  <Button
                    variant='outline'
                    onClick={closeExportModal}
                    disabled={isExporting}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleExportCsv}
                    loading={isExporting}
                    disabled={isExporting}
                  >
                    Esporta CSV
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value='hr' pt='md' keepMounted={false}>
          <Stack gap='md'>
            <Modal
              opened={trendHelpOpened}
              onClose={closeTrendHelpModal}
              title='Come leggere il grafico'
              centered
              size='lg'
            >
              <Stack gap='md'>
                <Text size='sm'>{TREND_SECTION_HELP_TEXT}</Text>
                <Text size='sm' fw={600}>
                  {trendFeatureInterpretation.short}
                </Text>
                <Text size='sm' c='dimmed'>
                  {trendFeatureInterpretation.detail}
                </Text>
                {trendNormalizationEnabled && (
                  <Text size='sm' c='dimmed'>
                    {TREND_NORMALIZATION_DISCLAIMER}
                  </Text>
                )}
                {trendLowPassEnabled && (
                  <Text size='sm' c='dimmed'>
                    {TREND_LOW_PASS_DISCLAIMER}
                  </Text>
                )}
                <Text size='sm' c='dimmed'>
                  {TREND_EXPORT_DISCLAIMER}
                </Text>
                <Text size='sm' c='dimmed'>
                  La tab <strong>Spettri</strong> è diversa: qui guardi
                  l’andamento nel tempo, lì guardi come il segnale si
                  distribuisce per oscillazioni e frequenze.
                </Text>
              </Stack>
            </Modal>

            <Card withBorder>
              <Stack gap='md'>
                <Stack gap={4}>
                  <Group justify='space-between' align='center' wrap='wrap'>
                    <Title order={4}>Evoluzione segnali pre-post VR</Title>
                    <Button
                      variant='light'
                      leftSection={<CircleHelp size={16} />}
                      onClick={openTrendHelpModal}
                    >
                      Come leggere il grafico
                    </Button>
                  </Group>
                  <Text size='sm' c='dimmed'>
                    {trendViewDescription}
                  </Text>
                  {overlayDataSourceBadge && (
                    <Badge
                      color={overlayDataSourceBadge.color}
                      variant='light'
                      w='fit-content'
                    >
                      {overlayDataSourceBadge.label}
                    </Badge>
                  )}
                </Stack>

                <Card withBorder padding='sm'>
                  <Group gap='md' align='flex-end' wrap='wrap'>
                    <Stack gap={4}>
                      <StatHelp
                        label='Segnale'
                        description={TREND_SIGNAL_TOOLTIP}
                      />
                      <Select
                        placeholder='Segnale'
                        variant='filled'
                        leftSection={<Rows3 size={14} />}
                        rightSection={<ChevronDown size={14} />}
                        data={featureKeys.map(feature => ({
                          value: feature,
                          label: trendFeatureLabels[feature],
                        }))}
                        value={selectedTrendFeature}
                        onChange={value =>
                          setSelectedTrendFeature((value as FeatureKey) || 'HR')
                        }
                        w={150}
                      />
                    </Stack>
                    <MantineTooltip
                      multiline
                      w={280}
                      label={TREND_NORMALIZATION_TOOLTIP}
                    >
                      <div>
                        <Switch
                          checked={trendNormalizationEnabled}
                          onChange={event =>
                            setTrendNormalizationEnabled(
                              event.currentTarget.checked
                            )
                          }
                          label='Normalizzazione'
                        />
                      </div>
                    </MantineTooltip>
                    <MantineTooltip
                      multiline
                      w={280}
                      label={TREND_LOW_PASS_TOOLTIP}
                    >
                      <div>
                        <Switch
                          checked={trendLowPassEnabled}
                          onChange={event =>
                            setTrendLowPassEnabled(event.currentTarget.checked)
                          }
                          label='Filtro passabasso'
                        />
                      </div>
                    </MantineTooltip>
                    <Switch
                      checked={showOverlaySessions}
                      onChange={event =>
                        setShowOverlaySessions(event.currentTarget.checked)
                      }
                      label='Mostra sessioni overlay'
                    />
                    <MultiSelect
                      placeholder={
                        overlayCompactValueLabel ?? 'Sessioni overlay'
                      }
                      variant='filled'
                      leftSection={<Filter size={14} />}
                      rightSection={<ChevronDown size={14} />}
                      data={overlaySessionSelectData}
                      value={overlayMultiSelectValue}
                      onChange={handleOverlaySessionChange}
                      searchable
                      clearable
                      disabled={!showOverlaySessions}
                      maxDropdownHeight={320}
                      styles={{
                        pill: {
                          display: shouldCompactOverlayPills
                            ? 'none'
                            : undefined,
                        },
                        inputField: {
                          minWidth: shouldCompactOverlayPills
                            ? '100%'
                            : undefined,
                        },
                      }}
                      w={320}
                    />
                  </Group>
                </Card>

                {isCnrCalabriaResearchUser ? (
                  <>
                    <Card withBorder padding='sm'>
                      <Group
                        justify='space-between'
                        align='flex-end'
                        wrap='wrap'
                      >
                        <Tabs
                          value={trendSegmentTab}
                          onChange={value =>
                            setTrendSegmentTab(
                              (value as 'full' | 'pre' | 'post') ?? 'full'
                            )
                          }
                        >
                          <Tabs.List>
                            <Tabs.Tab value='full'>Tratto completo</Tabs.Tab>
                            <Tabs.Tab value='pre'>Pre VR (Baseline)</Tabs.Tab>
                            <Tabs.Tab value='post'>Sessione VR</Tabs.Tab>
                          </Tabs.List>
                        </Tabs>
                        <NumberInput
                          label='Minuti baseline'
                          value={segmentWindowMinutes}
                          disabled
                          variant='filled'
                          w={160}
                        />
                      </Group>
                    </Card>

                    {effectiveValidationsLoading && (
                      <Text size='sm' c='dimmed'>
                        Aggiornamento segnali in corso…
                      </Text>
                    )}

                    {trendSegmentTab === 'full' && (
                      <Card withBorder>
                        <Stack gap='md'>
                          <Title order={5}>Tratto completo</Title>
                          {fullTrackSegmentOverlay && (
                            <Group gap='xs' wrap='wrap'>
                              <Badge color='blue' variant='light'>
                                Pre VR (Baseline):{' '}
                                {formatSecondLabel(
                                  fullTrackSegmentOverlay.preStart
                                )}
                                -
                                {formatSecondLabel(
                                  fullTrackSegmentOverlay.preEnd
                                )}
                              </Badge>
                              <Badge color='teal' variant='light'>
                                Sessione VR:{' '}
                                {formatSecondLabel(
                                  fullTrackSegmentOverlay.postStart
                                )}
                                -
                                {formatSecondLabel(
                                  fullTrackSegmentOverlay.postEnd
                                )}
                              </Badge>
                            </Group>
                          )}
                          <Group gap='md' align='flex-end' wrap='wrap'>
                            <Select
                              label='Inizio'
                              placeholder='Seleziona timestamp inizio'
                              variant='filled'
                              leftSection={<Clock3 size={14} />}
                              rightSection={<ChevronDown size={14} />}
                              data={trendStartOptions}
                              value={trendStart}
                              onChange={value => {
                                if (!value) {
                                  setTrendStart(null);
                                  return;
                                }
                                setTrendStart(value);
                                if (
                                  trendEnd &&
                                  Number(value) >= Number(trendEnd)
                                ) {
                                  const nextSecond = availableTrendSeconds.find(
                                    second => second > Number(value)
                                  );
                                  setTrendEnd(
                                    nextSecond != null
                                      ? nextSecond.toString()
                                      : null
                                  );
                                }
                              }}
                              clearable
                              w={220}
                            />
                            <Select
                              label='Fine'
                              placeholder='Seleziona timestamp fine'
                              variant='filled'
                              leftSection={<Clock3 size={14} />}
                              rightSection={<ChevronDown size={14} />}
                              data={trendEndOptions}
                              value={trendEnd}
                              onChange={setTrendEnd}
                              clearable
                              w={220}
                            />
                          </Group>
                          <div style={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer>
                              <LineChart data={filteredFullTrackRows}>
                                <CartesianGrid strokeDasharray='3 3' />
                                {fullTrackSegmentOverlay && (
                                  <>
                                    <ReferenceArea
                                      x1={fullTrackSegmentOverlay.preStart}
                                      x2={fullTrackSegmentOverlay.preEnd}
                                      fill='rgba(59, 130, 246, 0.12)'
                                      ifOverflow='extendDomain'
                                    />
                                    <ReferenceArea
                                      x1={fullTrackSegmentOverlay.postStart}
                                      x2={fullTrackSegmentOverlay.postEnd}
                                      fill='rgba(20, 184, 166, 0.12)'
                                      ifOverflow='extendDomain'
                                    />
                                  </>
                                )}
                                <XAxis
                                  type='number'
                                  dataKey='second'
                                  domain={['dataMin', 'dataMax']}
                                  ticks={fullTrackTimeTicks}
                                  tickFormatter={value =>
                                    formatSecondLabel(Number(value))
                                  }
                                />
                                <YAxis />
                                <Tooltip
                                  labelFormatter={formatTrendTooltipLabel}
                                  formatter={(value, name) => [
                                    formatTrendTooltipMetric(
                                      value as number | string
                                    ),
                                    String(name),
                                  ]}
                                />
                                <Legend />
                                <Area
                                  type='monotone'
                                  dataKey='stdUpper'
                                  stroke='none'
                                  fill='rgba(59,130,246,0.14)'
                                  activeDot={false}
                                />
                                <Area
                                  type='monotone'
                                  dataKey='stdLower'
                                  stroke='none'
                                  fill='rgba(255,255,255,0)'
                                  activeDot={false}
                                />
                                <Line
                                  type='monotone'
                                  dataKey='mean'
                                  stroke='#1d4ed8'
                                  strokeWidth={2.1}
                                  dot={false}
                                  name={`Media ${trendFeatureLabels[selectedTrendFeature]}`}
                                />
                                {renderOverlaySessionLines()}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          {(trendNormalizationEnabled ||
                            trendLowPassEnabled) && (
                            <Text size='sm' c='dimmed'>
                              {trendProcessingDisclaimerText}
                            </Text>
                          )}
                        </Stack>
                      </Card>
                    )}

                    {trendSegmentTab === 'pre' && (
                      <Card withBorder>
                        <Stack gap='md'>
                          <Title order={5}>Pre VR (Baseline)</Title>
                          <Group gap='md' align='flex-end' wrap='wrap'>
                            <Select
                              label='Inizio'
                              placeholder='Seleziona timestamp inizio'
                              variant='filled'
                              leftSection={<Clock3 size={14} />}
                              rightSection={<ChevronDown size={14} />}
                              data={preStartOptions}
                              value={preStart}
                              onChange={value => {
                                if (!value) {
                                  setPreStart(null);
                                  return;
                                }
                                setPreStart(value);
                                if (preEnd && Number(value) >= Number(preEnd)) {
                                  const nextSecond = segmentedTrendData.pre
                                    .map(row => row.second)
                                    .find(second => second > Number(value));
                                  setPreEnd(
                                    nextSecond != null
                                      ? nextSecond.toString()
                                      : null
                                  );
                                }
                              }}
                              clearable
                              w={220}
                            />
                            <Select
                              label='Fine'
                              placeholder='Seleziona timestamp fine'
                              variant='filled'
                              leftSection={<Clock3 size={14} />}
                              rightSection={<ChevronDown size={14} />}
                              data={preEndOptions}
                              value={preEnd}
                              onChange={setPreEnd}
                              clearable
                              w={220}
                            />
                          </Group>
                          <div style={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer>
                              <LineChart data={filteredPreSegmentRows}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis
                                  type='number'
                                  dataKey='second'
                                  domain={['dataMin', 'dataMax']}
                                  ticks={preSegmentTimeTicks}
                                  tickFormatter={value =>
                                    formatSecondLabel(Number(value))
                                  }
                                />
                                <YAxis />
                                <Tooltip
                                  labelFormatter={formatTrendTooltipLabel}
                                  formatter={(value, name) => [
                                    formatTrendTooltipMetric(
                                      value as number | string
                                    ),
                                    String(name),
                                  ]}
                                />
                                <Legend />
                                <Area
                                  type='monotone'
                                  dataKey='stdUpper'
                                  stroke='none'
                                  fill='rgba(59,130,246,0.14)'
                                  activeDot={false}
                                />
                                <Area
                                  type='monotone'
                                  dataKey='stdLower'
                                  stroke='none'
                                  fill='rgba(255,255,255,0)'
                                  activeDot={false}
                                />
                                <Line
                                  type='monotone'
                                  dataKey='mean'
                                  stroke='#1d4ed8'
                                  strokeWidth={2.1}
                                  dot={false}
                                  name={`Media ${trendFeatureLabels[selectedTrendFeature]}`}
                                />
                                {renderOverlaySessionLines()}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          {(trendNormalizationEnabled ||
                            trendLowPassEnabled) && (
                            <Text size='sm' c='dimmed'>
                              {trendProcessingDisclaimerText}
                            </Text>
                          )}
                        </Stack>
                      </Card>
                    )}

                    {trendSegmentTab === 'post' && (
                      <Card withBorder>
                        <Stack gap='md'>
                          <Title order={5}>Sessione VR</Title>
                          <Group gap='md' align='flex-end' wrap='wrap'>
                            <Select
                              label='Inizio'
                              placeholder='Seleziona timestamp inizio'
                              variant='filled'
                              leftSection={<Clock3 size={14} />}
                              rightSection={<ChevronDown size={14} />}
                              data={postStartOptions}
                              value={postStart}
                              onChange={value => {
                                if (!value) {
                                  setPostStart(null);
                                  return;
                                }
                                setPostStart(value);
                                if (
                                  postEnd &&
                                  Number(value) >= Number(postEnd)
                                ) {
                                  const nextSecond = segmentedTrendData.post
                                    .map(row => row.second)
                                    .find(second => second > Number(value));
                                  setPostEnd(
                                    nextSecond != null
                                      ? nextSecond.toString()
                                      : null
                                  );
                                }
                              }}
                              clearable
                              w={220}
                            />
                            <Select
                              label='Fine'
                              placeholder='Seleziona timestamp fine'
                              variant='filled'
                              leftSection={<Clock3 size={14} />}
                              rightSection={<ChevronDown size={14} />}
                              data={postEndOptions}
                              value={postEnd}
                              onChange={setPostEnd}
                              clearable
                              w={220}
                            />
                          </Group>
                          <div style={{ width: '100%', height: 320 }}>
                            <ResponsiveContainer>
                              <LineChart data={filteredPostSegmentRows}>
                                <CartesianGrid strokeDasharray='3 3' />
                                <XAxis
                                  type='number'
                                  dataKey='second'
                                  domain={['dataMin', 'dataMax']}
                                  ticks={postSegmentTimeTicks}
                                  tickFormatter={value =>
                                    formatSecondLabel(Number(value))
                                  }
                                />
                                <YAxis />
                                <Tooltip
                                  labelFormatter={formatTrendTooltipLabel}
                                  formatter={(value, name) => [
                                    formatTrendTooltipMetric(
                                      value as number | string
                                    ),
                                    String(name),
                                  ]}
                                />
                                <Legend />
                                <Area
                                  type='monotone'
                                  dataKey='stdUpper'
                                  stroke='none'
                                  fill='rgba(59,130,246,0.14)'
                                  activeDot={false}
                                />
                                <Area
                                  type='monotone'
                                  dataKey='stdLower'
                                  stroke='none'
                                  fill='rgba(255,255,255,0)'
                                  activeDot={false}
                                />
                                <Line
                                  type='monotone'
                                  dataKey='mean'
                                  stroke='#1d4ed8'
                                  strokeWidth={2.1}
                                  dot={false}
                                  name={`Media ${trendFeatureLabels[selectedTrendFeature]}`}
                                />
                                {renderOverlaySessionLines()}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          {(trendNormalizationEnabled ||
                            trendLowPassEnabled) && (
                            <Text size='sm' c='dimmed'>
                              {trendProcessingDisclaimerText}
                            </Text>
                          )}
                        </Stack>
                      </Card>
                    )}
                  </>
                ) : (
                  <>
                    <Card withBorder padding='sm'>
                      <Grid align='stretch'>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Card withBorder padding='sm' h='100%'>
                            <Stack gap='sm' h='100%' justify='space-between'>
                              <Text fw={600} size='sm'>
                                Segnale
                              </Text>
                              <Group
                                gap='md'
                                align='flex-end'
                                wrap='wrap'
                                mt='auto'
                              >
                                <Select
                                  label='Segnale'
                                  variant='filled'
                                  leftSection={<Rows3 size={14} />}
                                  rightSection={<ChevronDown size={14} />}
                                  data={featureKeys.map(feature => ({
                                    value: feature,
                                    label: trendFeatureLabels[feature],
                                  }))}
                                  value={selectedTrendFeature}
                                  onChange={value =>
                                    setSelectedTrendFeature(
                                      (value as FeatureKey) || 'HR'
                                    )
                                  }
                                  w={140}
                                />
                                <MantineTooltip
                                  multiline
                                  w={280}
                                  label={TREND_NORMALIZATION_TOOLTIP}
                                >
                                  <div>
                                    <Switch
                                      checked={trendNormalizationEnabled}
                                      onChange={event =>
                                        setTrendNormalizationEnabled(
                                          event.currentTarget.checked
                                        )
                                      }
                                      label='Normalizzazione'
                                    />
                                  </div>
                                </MantineTooltip>
                                <MantineTooltip
                                  multiline
                                  w={280}
                                  label={TREND_LOW_PASS_TOOLTIP}
                                >
                                  <div>
                                    <Switch
                                      checked={trendLowPassEnabled}
                                      onChange={event =>
                                        setTrendLowPassEnabled(
                                          event.currentTarget.checked
                                        )
                                      }
                                      label='Filtro passabasso'
                                    />
                                  </div>
                                </MantineTooltip>
                                <MultiSelect
                                  label='Sessioni overlay'
                                  placeholder={
                                    overlayCompactValueLabel ??
                                    'Seleziona sessioni'
                                  }
                                  variant='filled'
                                  leftSection={<Filter size={14} />}
                                  rightSection={<ChevronDown size={14} />}
                                  data={overlaySessionSelectData}
                                  value={overlayMultiSelectValue}
                                  onChange={handleOverlaySessionChange}
                                  searchable
                                  clearable
                                  disabled={!showOverlaySessions}
                                  maxDropdownHeight={320}
                                  styles={{
                                    pill: {
                                      display: shouldCompactOverlayPills
                                        ? 'none'
                                        : undefined,
                                    },
                                    inputField: {
                                      minWidth: shouldCompactOverlayPills
                                        ? '100%'
                                        : undefined,
                                    },
                                  }}
                                  w={320}
                                />
                                <Switch
                                  checked={showOverlaySessions}
                                  onChange={event =>
                                    setShowOverlaySessions(
                                      event.currentTarget.checked
                                    )
                                  }
                                  label='Mostra sessioni overlay'
                                />
                              </Group>
                            </Stack>
                          </Card>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Card withBorder padding='sm' h='100%'>
                            <Stack gap='sm' h='100%' justify='space-between'>
                              <Text fw={600} size='sm'>
                                Zoom timeline
                              </Text>
                              <Group
                                gap='md'
                                align='flex-end'
                                wrap='wrap'
                                mt='auto'
                              >
                                <Select
                                  label='Inizio'
                                  placeholder='Seleziona timestamp inizio'
                                  variant='filled'
                                  leftSection={<Clock3 size={14} />}
                                  rightSection={<ChevronDown size={14} />}
                                  data={trendStartOptions}
                                  value={trendStart}
                                  onChange={value => {
                                    if (!value) {
                                      setTrendStart(null);
                                      return;
                                    }
                                    setTrendStart(value);
                                    if (
                                      trendEnd &&
                                      Number(value) >= Number(trendEnd)
                                    ) {
                                      const nextSecond =
                                        availableTrendSeconds.find(
                                          second => second > Number(value)
                                        );
                                      setTrendEnd(
                                        nextSecond != null
                                          ? nextSecond.toString()
                                          : null
                                      );
                                    }
                                  }}
                                  clearable
                                  w={220}
                                />
                                <Select
                                  label='Fine'
                                  placeholder='Seleziona timestamp fine'
                                  variant='filled'
                                  leftSection={<Clock3 size={14} />}
                                  rightSection={<ChevronDown size={14} />}
                                  data={trendEndOptions}
                                  value={trendEnd}
                                  onChange={setTrendEnd}
                                  clearable
                                  w={220}
                                />
                              </Group>
                            </Stack>
                          </Card>
                        </Grid.Col>
                      </Grid>
                    </Card>
                    {effectiveValidationsLoading && (
                      <Text size='sm' c='dimmed' mb='sm'>
                        Aggiornamento segnali in corso…
                      </Text>
                    )}
                    <div style={{ width: '100%', height: 420 }}>
                      <ResponsiveContainer>
                        <LineChart data={filteredTrendChartData}>
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis
                            type='number'
                            dataKey='second'
                            domain={['dataMin', 'dataMax']}
                            ticks={trendTimeTicks}
                            tickFormatter={value =>
                              formatSecondLabel(Number(value))
                            }
                          />
                          <YAxis />
                          <Tooltip
                            labelFormatter={formatTrendTooltipLabel}
                            formatter={(value, name) => [
                              formatTrendTooltipMetric(
                                value as number | string
                              ),
                              String(name),
                            ]}
                          />
                          <Legend />
                          <Area
                            type='monotone'
                            dataKey='stdUpper'
                            stroke='none'
                            fill='rgba(59,130,246,0.14)'
                            activeDot={false}
                          />
                          <Area
                            type='monotone'
                            dataKey='stdLower'
                            stroke='none'
                            fill='rgba(255,255,255,0)'
                            activeDot={false}
                          />
                          <Line
                            type='monotone'
                            dataKey='mean'
                            stroke='#1d4ed8'
                            strokeWidth={2.1}
                            dot={false}
                            name={`Media ${trendFeatureLabels[selectedTrendFeature]}`}
                          />
                          {renderOverlaySessionLines()}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    {(trendNormalizationEnabled || trendLowPassEnabled) && (
                      <Text size='sm' c='dimmed'>
                        {trendProcessingDisclaimerText}
                      </Text>
                    )}
                  </>
                )}
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value='spectra' pt='md' keepMounted={false}>
          <Stack gap='md'>
            <Card withBorder>
              <Group justify='space-between' align='flex-start'>
                <Stack gap={4} maw={640}>
                  <Title order={4}>{SPECTRA_SECTION_TITLE}</Title>
                  <Text size='sm' c='dimmed'>
                    {SPECTRA_SECTION_CAPTION}
                  </Text>
                </Stack>
                <Group>
                  <Switch
                    checked={logScale}
                    onChange={event => setLogScale(event.currentTarget.checked)}
                    label='Scala log'
                  />
                  <Select
                    placeholder='Media gruppo'
                    clearable
                    data={snapshot.includedSessions.map(item => ({
                      value: item.validation.sessionId.toString(),
                      label: `${item.validation.sessionId} · ${item.validation.patientId}`,
                    }))}
                    value={selectedSessionId}
                    onChange={setSelectedSessionId}
                    w={240}
                  />
                </Group>
              </Group>
              <Group gap='md' align='flex-end' wrap='wrap' mt='md' mb='md'>
                <Select
                  label='Inizio'
                  placeholder='Seleziona timestamp inizio'
                  data={spectraStartOptions}
                  value={spectraStart}
                  onChange={value => {
                    if (!value) {
                      setSpectraStart(null);
                      return;
                    }

                    setSpectraStart(value);

                    if (spectraEnd && Number(value) >= Number(spectraEnd)) {
                      const nextFrequency = availableSpectraFrequencies.find(
                        frequency => frequency > Number(value)
                      );
                      setSpectraEnd(
                        nextFrequency != null ? nextFrequency.toString() : null
                      );
                    }
                  }}
                  clearable
                  w={220}
                />
                <Select
                  label='Fine'
                  placeholder='Seleziona timestamp fine'
                  data={spectraEndOptions}
                  value={spectraEnd}
                  onChange={value => {
                    if (!value) {
                      setSpectraEnd(null);
                      return;
                    }

                    setSpectraEnd(value);
                  }}
                  clearable
                  w={220}
                />
              </Group>
              <Grid mt='md'>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <Card withBorder>
                    <Title order={5}>PSD pre-VR</Title>
                    <div style={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <AreaChart data={filteredPsdData.baseline}>
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis dataKey='frequency' />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Area
                            type='monotone'
                            dataKey='lf'
                            name='PSD Welch'
                            stroke='#2563eb'
                            fill='rgba(37,99,235,0.25)'
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <Card withBorder>
                    <Title order={5}>PSD post-VR</Title>
                    <div style={{ width: '100%', height: 320 }}>
                      <ResponsiveContainer>
                        <AreaChart data={filteredPsdData.exposure}>
                          <CartesianGrid strokeDasharray='3 3' />
                          <XAxis dataKey='frequency' />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Area
                            type='monotone'
                            dataKey='lf'
                            name='PSD Welch'
                            stroke='#2563eb'
                            fill='rgba(37,99,235,0.25)'
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </Grid.Col>
              </Grid>
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value='statistics' pt='md'>
          <Stack gap='md'>
            <Card withBorder>
              <Stack gap='md'>
                <div>
                  <Title order={4}>Validazione statistica</Title>
                  <Text size='sm' c='dimmed'>
                    Questa sezione descrive la stabilità del gruppo nel segmento
                    selezionato. Non mostra un test pre/post: qui si legge
                    quanto i soggetti sono simili o variabili tra loro nel
                    tratto scelto.
                  </Text>
                </div>

                <Tabs
                  value={statisticalSegmentTab}
                  onChange={value =>
                    setStatisticalSegmentTab(
                      (value as StatisticalSegmentKey) ?? 'full'
                    )
                  }
                >
                  <Tabs.List>
                    {(
                      Object.keys(
                        statisticalSegmentLabels
                      ) as StatisticalSegmentKey[]
                    ).map(segment => (
                      <Tabs.Tab key={segment} value={segment}>
                        {statisticalSegmentLabels[segment]}
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                </Tabs>

                <Grid>
                  {descriptiveStatisticalValidation[statisticalSegmentTab].map(
                    row => (
                      <Grid.Col
                        key={`descriptive-stat-${statisticalSegmentTab}-${row.feature}`}
                        span={{ base: 12, md: 6 }}
                      >
                        <Card withBorder h='100%'>
                          <Stack gap='sm'>
                            <Group justify='space-between' align='flex-start'>
                              <div>
                                <Title order={5}>{row.feature}</Title>
                                <Text size='sm' c='dimmed'>
                                  {row.sessionCount} sessioni considerate nel
                                  segmento.
                                </Text>
                              </div>
                              <Badge variant='light'>
                                {
                                  statisticalSegmentLabels[
                                    statisticalSegmentTab
                                  ]
                                }
                              </Badge>
                            </Group>

                            <Grid>
                              <Grid.Col span={6}>
                                <StatHelp
                                  label='Media gruppo'
                                  description='Media dei valori medi calcolati per ogni sessione nel segmento selezionato.'
                                />
                                <Text fw={700}>
                                  {formatStatValue(row.mean)}
                                </Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <StatHelp
                                  label='Variabilità'
                                  description='Deviazione standard tra le sessioni: più è alta, più il gruppo è eterogeneo.'
                                />
                                <Text fw={700}>{formatStatValue(row.std)}</Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <StatHelp
                                  label='Range'
                                  description='Valore minimo e massimo osservati tra le medie delle sessioni.'
                                />
                                <Text fw={700}>
                                  {formatStatValue(row.min)} /{' '}
                                  {formatStatValue(row.max)}
                                </Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <StatHelp
                                  label='Coeff. variabilità'
                                  description='Rapporto percentuale tra deviazione standard e media: aiuta a capire quanto il gruppo varia rispetto al proprio livello medio.'
                                />
                                <Text fw={700}>
                                  {formatStatValue(
                                    row.coefficientOfVariation,
                                    '%'
                                  )}
                                </Text>
                              </Grid.Col>
                            </Grid>

                            <Divider />
                            <Stack gap={4}>
                              <StatHelp
                                label='Sessioni più distanti'
                                description='Sessioni con valore medio più lontano dalla media del gruppo nel segmento selezionato.'
                              />
                              {row.outlierSessions.length === 0 ? (
                                <Text size='sm' c='dimmed'>
                                  Nessuna sessione disponibile per questo
                                  segmento.
                                </Text>
                              ) : (
                                row.outlierSessions.map(session => (
                                  <Text
                                    key={`${row.feature}-${session.sessionId}`}
                                    size='sm'
                                  >
                                    Sessione {session.sessionId} ·{' '}
                                    {session.patientId}: valore{' '}
                                    {formatStatValue(session.value)}, distanza{' '}
                                    {formatStatValue(session.distance)}
                                  </Text>
                                ))
                              )}
                            </Stack>
                          </Stack>
                        </Card>
                      </Grid.Col>
                    )
                  )}
                </Grid>
              </Stack>
            </Card>

            <Card withBorder>
              <Title order={4} mb='xs'>
                Confronto pre/post salvato
              </Title>
              <Text size='sm' c='dimmed' mb='md'>
                Questo box usa la validazione statistica salvata nello snapshot:
                confronta Pre-VR e Sessione come analisi pre/post, quindi può
                mostrare p-value, effect size e normalità.
              </Text>

              {statisticalValidation.length === 0 ? (
                <Text c='dimmed'>
                  Servono almeno 3 sessioni valide per calcolare il confronto
                  pre/post salvato.
                </Text>
              ) : (
                <Tabs defaultValue={statisticalValidation[0]?.feature}>
                  <Tabs.List mb='md'>
                    {statisticalValidation.map(entry => (
                      <Tabs.Tab
                        key={`stats-tab-${entry.feature}`}
                        value={entry.feature}
                      >
                        {entry.feature}
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>

                  {statisticalValidation.map(entry => (
                    <Tabs.Panel
                      key={`stats-panel-${entry.feature}`}
                      value={entry.feature}
                    >
                      <Stack gap='sm'>
                        <Card withBorder>
                          <Group justify='space-between' align='flex-start'>
                            <div>
                              <Title order={5}>{entry.feature}</Title>
                              <Text size='sm' c='dimmed'>
                                Vista sintetica del cambiamento medio osservato
                                nel gruppo su questa metrica.
                              </Text>
                            </div>
                            <Group gap='xs'>
                              <MantineTooltip
                                multiline
                                w={260}
                                label={
                                  entry.pairedTest.significant
                                    ? 'Il cambiamento osservato nel gruppo risulta statisticamente rilevabile con la soglia usata.'
                                    : 'Il cambiamento osservato non emerge come statisticamente rilevabile con la soglia usata.'
                                }
                              >
                                <Badge
                                  color={significanceColor(
                                    entry.pairedTest.significant
                                  )}
                                  variant='light'
                                  style={{ cursor: 'help' }}
                                >
                                  {entry.pairedTest.significant
                                    ? 'p < 0.05'
                                    : 'n.s.'}
                                </Badge>
                              </MantineTooltip>
                              <MantineTooltip
                                multiline
                                w={260}
                                label='Indica quanto è grande il cambiamento osservato: negligible = quasi nullo, small = piccolo, medium = medio, large = marcato.'
                              >
                                <Badge
                                  variant='outline'
                                  style={{ cursor: 'help' }}
                                >
                                  {entry.pairedTest.effectSize}
                                </Badge>
                              </MantineTooltip>
                            </Group>
                          </Group>

                          <Grid mt='md'>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                              <Stack gap={4}>
                                <StatHelp
                                  label='Media iniziale'
                                  description='Valore medio della metrica nella fase iniziale del gruppo.'
                                />
                                <Text size='lg' fw={700}>
                                  {entry.pairedTest.baseline_mean}
                                </Text>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                              <Stack gap={4}>
                                <StatHelp
                                  label='Media finale'
                                  description='Valore medio della metrica nella fase finale del gruppo.'
                                />
                                <Text size='lg' fw={700}>
                                  {entry.pairedTest.exposure_mean}
                                </Text>
                              </Stack>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 4 }}>
                              <Stack gap={4}>
                                <StatHelp
                                  label='Delta medio'
                                  description='Differenza media tra fase finale e fase iniziale. Positivo = aumento, negativo = diminuzione.'
                                />
                                <Text size='lg' fw={700}>
                                  {entry.pairedTest.delta}
                                </Text>
                              </Stack>
                            </Grid.Col>
                          </Grid>
                        </Card>

                        <Grid>
                          <Grid.Col span={{ base: 12, md: 6 }}>
                            <Card withBorder h='100%'>
                              <Stack gap='xs'>
                                <StatHelp
                                  label='Affidabilità del risultato'
                                  description='Questi valori aiutano a capire se il cambiamento osservato è stabile e quanto è forte nel gruppo.'
                                />
                                <Text size='sm'>
                                  t-statistic: {entry.pairedTest.t_statistic}
                                </Text>
                                <Text size='sm'>
                                  p-value: {entry.pairedTest.p_value}
                                </Text>
                                <Text size='sm'>
                                  Cohen&apos;s d: {entry.pairedTest.cohens_d}
                                </Text>
                                <Text size='sm'>
                                  CI 95%: {entry.pairedTest.ci_95.lower} /{' '}
                                  {entry.pairedTest.ci_95.upper}
                                </Text>
                              </Stack>
                            </Card>
                          </Grid.Col>
                          <Grid.Col span={{ base: 12, md: 6 }}>
                            <Card withBorder h='100%'>
                              <Stack gap='xs'>
                                <StatHelp
                                  label='Distribuzione dei dati'
                                  description='Controlla se i dati del gruppo seguono una distribuzione sufficientemente regolare per leggere il test con maggiore tranquillità.'
                                />
                                <Text size='sm'>
                                  Normalità iniziale: p=
                                  {entry.normality.baseline_shapiro_p} (
                                  {entry.normality.baseline_normal
                                    ? 'ok'
                                    : 'non normale'}
                                  )
                                </Text>
                                <Text size='sm'>
                                  Normalità finale: p=
                                  {entry.normality.exposure_shapiro_p} (
                                  {entry.normality.exposure_normal
                                    ? 'ok'
                                    : 'non normale'}
                                  )
                                </Text>
                                <Text size='sm'>
                                  Normalità delta: p=
                                  {entry.normality.delta_shapiro_p} (
                                  {entry.normality.delta_normal
                                    ? 'ok'
                                    : 'non normale'}
                                  )
                                </Text>
                                {entry.normality.warning && (
                                  <Text size='sm' c='orange'>
                                    {entry.normality.warning}
                                  </Text>
                                )}
                              </Stack>
                            </Card>
                          </Grid.Col>
                        </Grid>
                      </Stack>
                    </Tabs.Panel>
                  ))}
                </Tabs>
              )}
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value='sessions' pt='md' keepMounted={false}>
          <Stack gap='md'>
            <Card withBorder>
              <Group>
                <Select
                  placeholder='Outlier'
                  clearable
                  data={[
                    { value: 'yes', label: 'Solo outlier' },
                    { value: 'no', label: 'Escludi outlier' },
                  ]}
                  value={outlierFilter}
                  onChange={setOutlierFilter}
                />
              </Group>
            </Card>

            <Card withBorder>
              <ScrollArea>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Paz.</Table.Th>
                      <Table.Th>Data</Table.Th>
                      <Table.Th>HR Δ</Table.Th>
                      <Table.Th>LF/HF Δ</Table.Th>
                      <Table.Th>RMSSD pre</Table.Th>
                      <Table.Th>RMSSD post</Table.Th>
                      <Table.Th>% corr.</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {filteredSessions.map(item => {
                      const expanded =
                        expandedSessionId === item.validation.sessionId;

                      return (
                        <>
                          <Table.Tr
                            key={item.validation.sessionId}
                            onClick={() =>
                              setExpandedSessionId(
                                expanded ? null : item.validation.sessionId
                              )
                            }
                            style={{ cursor: 'pointer' }}
                          >
                            <Table.Td>{item.validation.sessionId}</Table.Td>
                            <Table.Td>{item.validation.patientId}</Table.Td>
                            <Table.Td>
                              {formatDate(item.validation.sessionDate)}
                            </Table.Td>
                            <Table.Td>
                              {item.spectral.spectralMetrics.HR.delta}
                            </Table.Td>
                            <Table.Td>
                              {item.spectral.spectralMetrics.LFHF_ratio.delta}
                            </Table.Td>
                            <Table.Td>
                              {item.spectral.spectralMetrics.HRV.baseline.rmssd?.toFixed(
                                2
                              ) ?? '—'}
                            </Table.Td>
                            <Table.Td>
                              {item.spectral.spectralMetrics.HRV.exposure.rmssd?.toFixed(
                                2
                              ) ?? '—'}
                            </Table.Td>
                            <Table.Td>
                              {usesHrvPreprocessing(
                                item.spectral.preprocessingSummary
                              )
                                ? `${maxCorrectedBeatPercent(item.spectral.preprocessingSummary).toFixed(2)}%`
                                : '—'}
                            </Table.Td>
                          </Table.Tr>
                          {expanded && (
                            <Table.Tr
                              key={`${item.validation.sessionId}-expanded`}
                            >
                              <Table.Td colSpan={8}>
                                <Stack gap='sm'>
                                  <Text fw={600}>Cleaning log</Text>
                                  {item.validation.cleaningLog.map(log => (
                                    <Text key={log} size='sm' c='dimmed'>
                                      {log}
                                    </Text>
                                  ))}
                                  <Divider />
                                  <Text fw={600}>
                                    QC HRV (revisione manuale)
                                  </Text>
                                  <Checkbox
                                    label='Artefatti movimento/elettrodi revisionati (senza ECG)'
                                    checked={
                                      hrvQcBySessionId[
                                        item.validation.sessionId
                                      ]?.reviewed ?? false
                                    }
                                    onChange={event =>
                                      setHrvQcBySessionId(current => ({
                                        ...current,
                                        [item.validation.sessionId]: {
                                          reviewed: event.currentTarget.checked,
                                          notes:
                                            current[item.validation.sessionId]
                                              ?.notes ?? '',
                                        },
                                      }))
                                    }
                                  />
                                  <Textarea
                                    label='Note QC'
                                    placeholder='Es. artefatti movimento nel minuto 2–3 post-VR'
                                    minRows={2}
                                    value={
                                      hrvQcBySessionId[
                                        item.validation.sessionId
                                      ]?.notes ?? ''
                                    }
                                    onChange={event =>
                                      setHrvQcBySessionId(current => ({
                                        ...current,
                                        [item.validation.sessionId]: {
                                          reviewed:
                                            current[item.validation.sessionId]
                                              ?.reviewed ?? false,
                                          notes: event.currentTarget.value,
                                        },
                                      }))
                                    }
                                  />
                                  <Divider />
                                  <Text fw={600}>Metriche</Text>
                                  {featureKeys.map(feature => (
                                    <Text key={feature} size='sm'>
                                      {feature}: baseline mean{' '}
                                      {
                                        item.validation.metrics[feature]
                                          .baseline.mean
                                      }{' '}
                                      | exposure mean{' '}
                                      {
                                        item.validation.metrics[feature]
                                          .exposure.mean
                                      }{' '}
                                      | delta{' '}
                                      {item.validation.metrics[feature].delta}
                                    </Text>
                                  ))}
                                </Stack>
                              </Table.Td>
                            </Table.Tr>
                          )}
                        </>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Card>

            {snapshot.excludedSessions.length > 0 && (
              <Card withBorder>
                <Title order={5} mb='sm'>
                  Sessioni escluse
                </Title>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>ID</Table.Th>
                      <Table.Th>Utente</Table.Th>
                      <Table.Th>Data</Table.Th>
                      <Table.Th>Motivo</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {snapshot.excludedSessions.map(item => (
                      <Table.Tr key={item.sessionId}>
                        <Table.Td>{item.sessionId}</Table.Td>
                        <Table.Td>{item.patientId}</Table.Td>
                        <Table.Td>{formatDate(item.sessionDate)}</Table.Td>
                        <Table.Td>{item.reason}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={editSessionsOpened}
        onClose={closeEditSessions}
        title='Modifica sessioni analisi'
        size='80vw'
        centered
      >
        <Stack gap='md'>
          <Text size='sm' c='dimmed'>
            Aggiungi o rimuovi sessioni completed. Al salvataggio l’analisi
            viene ricalcolata sulla stessa URL usando dati riparati se
            disponibili.
          </Text>

          <Card withBorder>
            <Stack gap='sm'>
              <Group grow>
                <TextInput
                  label='Cerca per ID'
                  value={editSearchId}
                  onChange={event => {
                    setEditSearchId(event.currentTarget.value);
                    setEditPage(1);
                  }}
                  leftSection={<Filter size={16} />}
                />
                <TextInput
                  label='Cerca per utente'
                  value={editSearchPatient}
                  onChange={event => {
                    setEditSearchPatient(event.currentTarget.value);
                    setEditPage(1);
                  }}
                />
                <TextInput
                  label='Cerca per data'
                  value={editSearchDate}
                  onChange={event => {
                    setEditSearchDate(event.currentTarget.value);
                    setEditPage(1);
                  }}
                />
              </Group>

              <Group justify='space-between' align='flex-start'>
                <Stack gap={4}>
                  <Text size='sm' c='dimmed'>
                    {editSelectedSessionIds.length} sessioni selezionate. Minimo
                    richiesto: 3 sessioni.
                  </Text>
                  <Group gap='xs' wrap='wrap'>
                    <Badge color='teal' variant='light'>
                      +{editAddedSessionIds.length} aggiunte
                    </Badge>
                    <Badge color='red' variant='light'>
                      -{editRemovedSessionIds.length} rimosse
                    </Badge>
                  </Group>
                  {(editAddedSessionIds.length > 0 ||
                    editRemovedSessionIds.length > 0) && (
                    <Text size='xs' c='dimmed'>
                      Aggiunte:{' '}
                      {editAddedSessionIds.length > 0
                        ? editAddedSessionIds.join(', ')
                        : 'nessuna'}{' '}
                      | Rimosse:{' '}
                      {editRemovedSessionIds.length > 0
                        ? editRemovedSessionIds.join(', ')
                        : 'nessuna'}
                    </Text>
                  )}
                </Stack>
                <Button
                  variant='subtle'
                  size='compact-sm'
                  onClick={() =>
                    setEditSelectedSessionIds(current =>
                      Array.from(
                        new Set([
                          ...current,
                          ...editFilteredSessions.map(session => session.id),
                        ])
                      )
                    )
                  }
                >
                  Seleziona pagina corrente
                </Button>
              </Group>

              <ScrollArea h={360}>
                {editSessionsLoading ? (
                  <Group justify='center' py='xl'>
                    <Loader size='sm' />
                  </Group>
                ) : (
                  <Stack gap='sm'>
                    {editSessionsFetching && (
                      <Group justify='center'>
                        <Loader size='xs' />
                      </Group>
                    )}
                    <Table striped highlightOnHover stickyHeader>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th />
                          <Table.Th>ID</Table.Th>
                          <Table.Th>Utente</Table.Th>
                          <Table.Th>Baseline</Table.Th>
                          <Table.Th>Record biometrici</Table.Th>
                          <Table.Th>Riparata</Table.Th>
                          <Table.Th>Scenario</Table.Th>
                          <Table.Th>Data</Table.Th>
                          <Table.Th>Stato</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {editFilteredSessions.map(session => {
                          const selected = editSelectedSessionIds.includes(
                            session.id
                          );
                          const baselineAvailable = hasBaselineValue(
                            session.baseline
                          );

                          return (
                            <Table.Tr key={session.id}>
                              <Table.Td>
                                <Checkbox
                                  checked={selected}
                                  onChange={() => toggleEditSession(session.id)}
                                />
                              </Table.Td>
                              <Table.Td>{session.id}</Table.Td>
                              <Table.Td>
                                {session.patientCode ?? 'n/a'}
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={baselineAvailable ? 'teal' : 'gray'}
                                  variant='light'
                                >
                                  {baselineAvailable
                                    ? 'Tracciata'
                                    : 'Non tracciata'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={session.hasHrData ? 'teal' : 'red'}
                                  variant='light'
                                >
                                  {session.hasHrData ? 'Presenti' : 'Assenti'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={
                                    session.hasHrDataRepaired ? 'blue' : 'gray'
                                  }
                                  variant='light'
                                >
                                  {session.hasHrDataRepaired ? 'Si' : 'No'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>{session.scenario}</Table.Td>
                              <Table.Td>
                                {formatDate(
                                  session.started_at ??
                                    session.created_at ??
                                    session.updated_at
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Badge variant='light'>{session.status}</Badge>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                    {editConfigurationsLite &&
                      editConfigurationsLite.totalPages > 1 && (
                        <Group justify='space-between' align='center'>
                          <Text size='xs' c='dimmed'>
                            {editConfigurationsLite.total} sessioni completed
                            trovate
                          </Text>
                          <Pagination
                            value={editPage}
                            onChange={setEditPage}
                            total={editConfigurationsLite.totalPages}
                            size='sm'
                          />
                        </Group>
                      )}
                  </Stack>
                )}
              </ScrollArea>
            </Stack>
          </Card>

          <Group justify='flex-end'>
            <Button
              variant='subtle'
              onClick={closeEditSessions}
              disabled={isUpdatingSessions}
            >
              Annulla
            </Button>
            <Button
              loading={isUpdatingSessions}
              onClick={() => void handleUpdateAnalysisSessions()}
              disabled={editSelectedSessionIds.length < 3}
            >
              Ricalcola e salva
            </Button>
          </Group>
        </Stack>
      </Modal>

      {snapshot && (
        <>
          <CSVLink
            ref={overviewCsvRef}
            data={overviewCsvData}
            filename={buildExportFileName('overview')}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={sessionsCsvRef}
            data={sessionsCsvData}
            filename={buildExportFileName('sessioni')}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={trendHrCsvRef}
            data={trendHrCsvData}
            filename={buildExportFileName(`evoluzione-hr_${trendModeLabel}`)}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={trendHrvCsvRef}
            data={trendHrvCsvData}
            filename={buildExportFileName(`evoluzione-hrv_${trendModeLabel}`)}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={trendLfCsvRef}
            data={trendLfCsvData}
            filename={buildExportFileName(`evoluzione-lf_${trendModeLabel}`)}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={trendHfCsvRef}
            data={trendHfCsvData}
            filename={buildExportFileName(`evoluzione-hf_${trendModeLabel}`)}
            style={{ display: 'none' }}
          />
          <CSVLink
            ref={spectraCsvRef}
            data={spectraCsvData}
            filename={buildExportFileName('spettri')}
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );
}

export default SpectralNormalizationDetailPage;
